<template>
  <div>
    <AppNavbar />
    <section class="register-section">
      <div class="container">
        <h2 class="title">
          {{
            selectedRole === "distributor"
              ? "DISTRIBUTOR REGISTRATION"
              : "CREATE ACCOUNT"
          }}
        </h2>
        <div class="card-box register-box">
          <!-- ─── CUSTOMER FLOW ─── -->
          <template v-if="selectedRole === 'customer'">
            <h3 class="role-form-title">Customer Registration</h3>
            <form @submit.prevent="handleRegister">
              <div class="form-grid">
                <div class="form-group">
                  <label>Full Name</label>
                  <input
                    v-model="firstName"
                    type="text"
                    placeholder="Full Name"
                    :disabled="customerOtpRequired"
                  />
                </div>
                <div class="form-group">
                  <label>Mobile Number</label>
                  <input
                    v-model="mobileNumber"
                    type="text"
                    placeholder="Phone Number"
                    :disabled="customerOtpRequired"
                  />
                </div>
                <div class="form-group">
                  <label>Email</label>
                  <input
                    v-model="email"
                    type="email"
                    placeholder="Email"
                    :disabled="customerOtpRequired"
                  />
                </div>
                <div class="form-group">
                  <label>Password</label>
                  <div class="password-wrapper">
                    <input
                      v-model="password"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="Enter your password"
                      :disabled="customerOtpRequired"
                    />
                    <button
                      type="button"
                      class="password-visibility-btn"
                      @click="togglePasswordVisibility"
                    >
                      <i
                        :class="
                          showPassword ? 'ri-eye-off-line' : 'ri-eye-line'
                        "
                      ></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Create Account button — hidden once OTP has been sent -->
              <button
                v-if="!customerOtpRequired"
                class="register-btn"
                type="submit"
                :disabled="isLoading"
              >
                {{ isLoading ? "CREATING..." : "CREATE ACCOUNT" }}
              </button>

              <!-- OTP CONFIRMATION BLOCK -->
              <div v-if="customerOtpRequired" class="otp-confirm-wrap">
                <div class="otp-sent-banner">
                  <i class="ri-mail-send-line"></i>
                  <span>OTP sent to your registered mobile number.</span>
                </div>

                <div class="form-group" style="margin-bottom: 0">
                  <label>Enter OTP <span class="required">*</span></label>
                  <div class="otp-row">
                    <input
                      v-model="customerOtp"
                      type="text"
                      maxlength="6"
                      placeholder="Enter OTP"
                      class="otp-input"
                    />
                    <button
                      type="button"
                      class="verify-btn"
                      :disabled="isOtpLoading || customerOtp.length < 4"
                      @click="handleCustomerOtpConfirm"
                    >
                      {{ isOtpLoading ? "Verifying..." : "Confirm OTP" }}
                    </button>
                  </div>
                </div>

                <div class="otp-action-row">
                  <button
                    type="button"
                    class="resend-btn"
                    :disabled="isResendOtpLoading"
                    @click="handleCustomerOtpResend"
                  >
                    {{ isResendOtpLoading ? "Resending..." : "Resend OTP" }}
                  </button>
                </div>
              </div>
            </form>
            <div v-if="toastMessage" :class="['toast', toastType]">
              {{ toastMessage }}
            </div>
          </template>

          <!-- ─── DISTRIBUTOR WIZARD ─── -->
          <template v-else>
            <!-- WIZARD PROGRESS -->
            <div class="wizard-progress">
              <div
                v-for="(step, i) in wizardSteps"
                :key="i"
                class="wizard-progress-item"
              >
                <div
                  class="wizard-step-dot"
                  :class="{
                    active: currentWizardStep === i + 1,
                    completed: isWizardStepCompleted(i),
                    skipped: isWizardStepSkipped(i),
                  }"
                >
                  <i v-if="isWizardStepCompleted(i)" class="ri-check-line"></i>
                  <i
                    v-else-if="isWizardStepSkipped(i)"
                    class="ri-time-line"
                  ></i>
                  <span v-else>{{ i + 1 }}</span>
                </div>
                <span
                  class="wizard-step-label"
                  :class="{
                    active: currentWizardStep === i + 1,
                    completed: isWizardStepCompleted(i),
                    skipped: isWizardStepSkipped(i),
                  }"
                  >{{ step }}</span
                >
                <div
                  v-if="i < wizardSteps.length - 1"
                  class="wizard-step-line"
                  :class="{
                    active: isWizardStepCompleted(i),
                    skipped: isWizardStepSkipped(i),
                  }"
                ></div>
              </div>
            </div>

            <!-- ── STEP 1 + 2: Email then Mobile/Identity ── -->
            <div
              v-if="distStep === 1 || distStep === 2"
              class="wizard-step-content"
            >
              <div class="step-header">
                <h3 class="step-title" v-if="distStep === 1">
                  Identity Verification
                </h3>
                <h3 class="step-title" v-else>Identity Verification</h3>
                <p class="step-subtitle" v-if="distStep === 1">
                  Step 1: Verify your email to begin identity verification.
                </p>
                <p class="step-subtitle" v-else>
                  Complete referral and identity verification details.
                </p>
              </div>

              <!-- Email OTP -->
              <div
                v-if="distStep === 1"
                class="form-group"
                style="margin-bottom: 20px"
              >
                <label>Email Address <span class="required">*</span></label>
                <div class="input-verify-row">
                  <input
                    v-model="dist.email"
                    type="email"
                    placeholder="you@example.com"
                    :disabled="emailVerified"
                    :class="{ 'input-verified': emailVerified }"
                  />
                  <button
                    v-if="!emailVerified && !emailOtpSent"
                    type="button"
                    class="verify-btn"
                    :disabled="!dist.email.trim() || emailOtpLoading"
                    @click="sendEmailOtp"
                  >
                    {{ emailOtpLoading ? "Sending..." : "Send OTP" }}
                  </button>
                  <span v-if="emailVerified" class="verified-badge"
                    ><i class="ri-checkbox-circle-fill"></i> Verified</span
                  >
                </div>
                <div
                  v-if="emailOtpSent && !emailVerified"
                  class="otp-row"
                  style="margin-top: 10px"
                >
                  <input
                    v-model="emailOtp"
                    type="text"
                    placeholder="Enter OTP"
                    maxlength="6"
                    class="otp-input"
                  />
                  <button
                    type="button"
                    class="verify-btn"
                    :disabled="emailOtp.length < 4"
                    @click="verifyEmailOtp"
                  >
                    Confirm OTP
                  </button>
                  <button
                    type="button"
                    class="resend-btn"
                    @click="sendEmailOtp"
                  >
                    Resend
                  </button>
                </div>
                <p v-if="emailError" class="field-error">{{ emailError }}</p>
                <p v-else-if="emailApiResponse" class="field-success">
                  {{ emailApiResponse }}
                </p>
              </div>

              <div v-if="distStep === 1" class="step-actions">
                <button
                  type="button"
                  class="next-btn"
                  :disabled="!emailVerified"
                  @click="goToStep2"
                >
                  Continue
                  <i class="ri-arrow-right-line"></i>
                </button>
              </div>

              <!-- Referrer ID — shown first for non-imported users -->
              <transition name="slide-down">
                <div v-if="distStep === 2 && emailVerified">
                  <div
                    v-if="importedReferenceCode"
                    class="form-group"
                    style="margin-bottom: 24px"
                  >
                    <label>Reference Code</label>
                    <div class="input-verify-row">
                      <input
                        :value="importedReferenceCode"
                        type="text"
                        disabled
                        class="input-verified"
                      />
                      <span class="verified-badge"
                        ><i class="ri-checkbox-circle-fill"></i> Verified
                      </span>
                    </div>
                    <p v-if="referrerMaskedName" class="referrer-name-reveal">
                      <i class="ri-user-star-fill"></i> Referred by
                      <strong>{{ referrerMaskedName }}</strong>
                    </p>
                  </div>

                  <div
                    v-if="!importedReferenceCode"
                    class="form-group"
                    style="margin-bottom: 24px"
                  >
                    <label>Referrer ID <span class="required">*</span></label>
                    <div class="input-verify-row">
                      <input
                        v-model="dist.referrerId"
                        type="text"
                        placeholder="e.g. DIST-10045"
                        :class="{
                          'input-verified': referrerVerified,
                          'input-error': referrerError,
                        }"
                        :disabled="referrerVerified"
                      />
                      <button
                        v-if="!referrerVerified"
                        type="button"
                        class="verify-btn"
                        :disabled="!dist.referrerId.trim() || referrerLoading"
                        @click="verifyReferrer"
                      >
                        {{ referrerLoading ? "Checking..." : "Verify" }}
                      </button>
                      <span v-if="referrerVerified" class="verified-badge"
                        ><i class="ri-checkbox-circle-fill"></i> Verified</span
                      >
                    </div>
                    <p v-if="referrerError" class="field-error">
                      {{ referrerError }}
                    </p>
                    <p v-if="referrerVerified" class="referrer-name-reveal">
                      <i class="ri-user-star-fill"></i> Referred by
                      <strong>{{ referrerMaskedName }}</strong>
                    </p>
                  </div>

                  <!-- Identity fields — revealed after referrer (or immediately for imported users) -->
                  <transition name="slide-down">
                    <div
                      v-if="referrerVerified || !!importedReferenceCode"
                      class="identity-fields"
                    >
                      <div class="form-grid">
                        <div class="form-group">
                          <label
                            >Full Name <span class="required">*</span></label
                          >
                          <input
                            v-model="dist.fullName"
                            type="text"
                            placeholder="As on Aadhaar"
                          />
                        </div>
                        <div class="form-group">
                          <label
                            >Date of Birth
                            <span class="required">*</span></label
                          >
                          <input
                            v-model="dist.dob"
                            type="date"
                            :max="maxDob"
                            style="cursor: pointer"
                          />
                        </div>

                        <!-- Mobile OTP -->
                        <div
                          v-if="distStep === 2"
                          class="form-group"
                          style="margin-bottom: 20px"
                        >
                          <label
                            >Mobile Number
                            <span class="required">*</span></label
                          >
                          <div class="input-verify-row">
                            <input
                              v-model="dist.mobile"
                              type="text"
                              placeholder="Enter 10-digit mobile number"
                              maxlength="10"
                              :disabled="mobileVerified"
                              :class="{ 'input-verified': mobileVerified }"
                            />
                            <button
                              v-if="!mobileVerified && !mobileOtpSent"
                              type="button"
                              class="verify-btn"
                              :disabled="
                                !emailVerified ||
                                dist.mobile.length < 10 ||
                                mobileOtpLoading
                              "
                              @click="sendMobileOtp"
                            >
                              {{ mobileOtpLoading ? "Sending..." : "Send OTP" }}
                            </button>
                            <span v-if="mobileVerified" class="verified-badge"
                              ><i class="ri-checkbox-circle-fill"></i>
                              Verified</span
                            >
                          </div>
                          <div
                            v-if="mobileOtpSent && !mobileVerified"
                            class="otp-row"
                            style="margin-top: 10px"
                          >
                            <input
                              v-model="mobileOtp"
                              type="text"
                              placeholder="Enter OTP"
                              maxlength="6"
                              class="otp-input"
                            />
                            <button
                              type="button"
                              class="verify-btn"
                              :disabled="mobileOtp.length < 4"
                              @click="verifyMobileOtp"
                            >
                              Confirm OTP
                            </button>
                            <button
                              type="button"
                              class="resend-btn"
                              @click="sendMobileOtp"
                            >
                              Resend
                            </button>
                          </div>
                          <p v-if="mobileError" class="field-error">
                            {{ mobileError }}
                          </p>
                          <p
                            v-else-if="mobileApiResponse"
                            class="field-success"
                          >
                            {{ mobileApiResponse }}
                          </p>
                          <p
                            v-if="!emailVerified"
                            class="field-hint"
                            style="margin-top: 8px"
                          >
                            Verify email first to continue with mobile OTP.
                          </p>
                        </div>
                        <div class="form-group">
                          <label
                            >Password <span class="required">*</span></label
                          >
                          <div class="password-wrapper">
                            <input
                              v-model="dist.password"
                              :type="showDistPassword ? 'text' : 'password'"
                              placeholder="Min. 8 characters"
                            />
                            <button
                              type="button"
                              class="password-visibility-btn"
                              @click="showDistPassword = !showDistPassword"
                            >
                              <i
                                :class="
                                  showDistPassword
                                    ? 'ri-eye-off-line'
                                    : 'ri-eye-line'
                                "
                              ></i>
                            </button>
                          </div>
                          <p class="field-hint">
                            Min. 8 chars, 1 uppercase, 1 number.
                          </p>
                        </div>
                        <div class="form-group">
                          <label
                            >Confirm Password
                            <span class="required">*</span></label
                          >
                          <div class="password-wrapper">
                            <input
                              v-model="dist.confirmPassword"
                              :type="
                                showDistConfirmPassword ? 'text' : 'password'
                              "
                              placeholder="Re-enter password"
                            />
                            <button
                              type="button"
                              class="password-visibility-btn"
                              @click="
                                showDistConfirmPassword =
                                  !showDistConfirmPassword
                              "
                            >
                              <i
                                :class="
                                  showDistConfirmPassword
                                    ? 'ri-eye-off-line'
                                    : 'ri-eye-line'
                                "
                              ></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <p v-if="step1Error" class="step-error">
                        {{ step1Error }}
                      </p>

                      <div class="step-actions">
                        <button
                          type="button"
                          class="next-btn"
                          :disabled="
                            step1Saving ||
                            !mobileVerified ||
                            !dist.password.trim() ||
                            !dist.confirmPassword.trim()
                          "
                          @click="goToStep3"
                        >
                          {{
                            step1Saving
                              ? "Saving Details..."
                              : "Continue to KYC Verification"
                          }}
                          <i class="ri-arrow-right-line"></i>
                        </button>
                      </div>
                    </div>
                  </transition>
                </div>
              </transition>
            </div>

            <!-- ── STEP 3: Aadhaar + PAN ── -->
            <div v-if="distStep === 3" class="wizard-step-content">
              <div class="step-header">
                <h3 class="step-title">KYC Verification</h3>
                <p class="step-subtitle">
                  Verify your Aadhaar and PAN for compliance and account
                  activation.
                </p>
              </div>

              <!-- Aadhaar -->
              <div class="section-divider" style="margin-top: 0">
                <span>Aadhaar Verification</span>
              </div>

              <div class="form-group">
                <label>Aadhaar Number <span class="required">*</span></label>
                <div class="input-verify-row">
                  <input
                    v-model="dist.aadhaar"
                    type="text"
                    placeholder="XXXX XXXX XXXX"
                    maxlength="14"
                    :disabled="aadhaarVerified || aadhaarOtpVerifying"
                    :class="{
                      'input-verified': aadhaarVerified,
                      'input-error': aadhaarError,
                    }"
                    @input="formatAadhaar"
                  />
                  <button
                    v-if="!aadhaarVerified && !aadhaarOtpSent"
                    type="button"
                    class="verify-btn"
                    :disabled="
                      dist.aadhaar.replace(/\s/g, '').length < 12 ||
                      aadhaarLoading ||
                      aadhaarOtpVerifying ||
                      isDigilockerFlowBlocking
                    "
                    @click="sendAadhaarOtp"
                  >
                    {{
                      aadhaarLoading ? "Sending..." : "Verify via Aadhaar OTP"
                    }}
                  </button>
                  <button
                    v-if="!aadhaarVerified && !aadhaarOtpSent"
                    type="button"
                    class="verify-btn digilocker-btn"
                    :disabled="
                      dist.aadhaar.replace(/\s/g, '').length < 12 ||
                      aadhaarLoading ||
                      aadhaarOtpVerifying ||
                      isDigilockerFlowBlocking
                    "
                    @click="initiateDigilockerVerification"
                  >
                    {{
                      digilockerInitiating
                        ? "Opening..."
                        : digilockerStatusChecking
                        ? "Checking..."
                        : "Verify via DigiLocker"
                    }}
                  </button>
                  <span v-if="aadhaarVerified" class="verified-badge"
                    ><i class="ri-checkbox-circle-fill"></i> Verified</span
                  >
                </div>
                <p v-if="aadhaarError" class="field-error">
                  {{ aadhaarError }}
                </p>
                <p v-else-if="aadhaarStatusMessage" class="field-success">
                  {{ aadhaarStatusMessage }}
                </p>
              </div>

              <div
                v-if="aadhaarOtpSent && !aadhaarVerified"
                class="otp-row"
                style="margin-top: 10px"
              >
                <input
                  v-model="aadhaarOtp"
                  type="text"
                  placeholder="Enter OTP sent to Aadhaar-linked mobile"
                  maxlength="6"
                  class="otp-input"
                  :disabled="aadhaarOtpVerifying"
                />
                <button
                  type="button"
                  class="verify-btn"
                  :disabled="aadhaarOtp.length < 6 || aadhaarOtpVerifying"
                  @click="verifyAadhaarOtp"
                >
                  {{ aadhaarOtpVerifying ? "Verifying..." : "Confirm OTP" }}
                </button>
                <button
                  type="button"
                  class="resend-btn"
                  :disabled="aadhaarLoading || aadhaarOtpVerifying"
                  @click="sendAadhaarOtp"
                >
                  Resend
                </button>
              </div>

              <!-- Address-fetch loading state (shown right after OTP verifies,
                   before the address is revealed) -->
              <transition name="slide-down">
                <div
                  v-if="aadhaarAddressLoading"
                  class="aadhaar-address-loading"
                >
                  <i class="ri-loader-4-line spin"></i>
                  <span>Fetching your address from Aadhaar...</span>
                </div>
              </transition>

              <!-- Address from Aadhaar -->
              <transition name="slide-down">
                <div
                  v-if="
                    aadhaarVerified &&
                    !aadhaarAddressLoading &&
                    getAadhaarAddressText(aadhaarAddress)
                  "
                  class="aadhaar-address-box"
                >
                  <div class="aadhaar-address-header">
                    <i class="ri-map-pin-2-fill"></i>
                    <span>Address fetched from Aadhaar</span>
                  </div>
                  <p class="aadhaar-address-text">
                    {{ getAadhaarAddressText(aadhaarAddress) }}
                  </p>
                  <div class="address-save-options">
                    <div class="checkbox-row">
                      <label class="check-option">
                        <input type="checkbox" v-model="saveAsBilling" />
                        <span>Save this address.</span>
                      </label>
                    </div>
                    <p class="field-hint" style="margin-top: 8px">
                      <i class="ri-information-line"></i>
                      You can update or add a new address anytime from your
                      profile.
                    </p>
                  </div>
                </div>
              </transition>

              <!-- PAN — only unlocked once Aadhaar is verified in this
                   current flow and its address has finished loading -->
              <transition name="slide-down">
                <div v-if="canShowPanSection">
                  <div class="section-divider">
                    <span>PAN Verification</span>
                  </div>

                  <div class="form-group pan-type-group">
                    <label>PAN Type <span class="required">*</span></label>
                    <div class="pan-type-options">
                      <label class="pan-type-option">
                        <input
                          type="radio"
                          name="panApplicantType"
                          :checked="panApplicantType === 'individual'"
                          @change="selectPanApplicantType('individual')"
                        />
                        <span>Individual</span>
                      </label>
                      <label class="pan-type-option">
                        <input
                          type="radio"
                          name="panApplicantType"
                          :checked="panApplicantType === 'huf'"
                          @change="selectPanApplicantType('huf')"
                        />
                        <span>HUF</span>
                      </label>
                    </div>
                  </div>

                  <div v-if="isHufSelected" class="form-grid pan-huf-grid">
                    <div class="form-group">
                      <label>HUF Name <span class="required">*</span></label>
                      <input
                        v-model="dist.hufName"
                        type="text"
                        placeholder="Enter HUF name"
                        :disabled="isKycFullyVerified"
                      />
                    </div>
                    <div class="form-group">
                      <label
                        >HUF Date of Incorporation
                        <span class="required">*</span></label
                      >
                      <input
                        v-model="dist.hufDateOfIncorporation"
                        type="date"
                        :max="maxDob"
                        :disabled="isKycFullyVerified"
                        style="cursor: pointer"
                      />
                    </div>
                  </div>

                  <div class="form-group">
                    <label>PAN Number <span class="required">*</span></label>
                    <div class="input-verify-row">
                      <input
                        v-model="dist.pan"
                        type="text"
                        placeholder="ABCDE1234F"
                        maxlength="10"
                        :disabled="isKycFullyVerified"
                        :class="{
                          'input-verified': isKycFullyVerified,
                          'input-error': panError,
                        }"
                        @input="dist.pan = dist.pan.toUpperCase()"
                      />
                      <button
                        v-if="!panVerified"
                        type="button"
                        class="verify-btn"
                        :disabled="!canVerifyPan"
                        @click="verifyPan"
                      >
                        {{ panLoading ? "Verifying..." : "Verify PAN" }}
                      </button>
                      <span v-if="isKycFullyVerified" class="verified-badge"
                        ><i class="ri-checkbox-circle-fill"></i> Verified</span
                      >
                    </div>
                    <p v-if="panError" class="field-error">{{ panError }}</p>
                  </div>

                  <transition name="slide-down">
                    <div v-if="isKycFullyVerified" class="verified-info-box">
                      <i class="ri-shield-check-fill"></i>
                      <div>
                        <p class="verified-info-sub">
                          PAN verified successfully
                        </p>
                      </div>
                    </div>
                  </transition>
                </div>
              </transition>

              <p v-if="step2Error" class="step-error">{{ step2Error }}</p>

              <div class="step-actions two-col">
                <button
                  type="button"
                  class="back-btn"
                  @click="handleKycStepBack"
                >
                  <i class="ri-arrow-left-line"></i> Back
                </button>
                <button
                  type="button"
                  class="next-btn"
                  :disabled="!aadhaarVerified || !panVerified || step2Saving"
                  @click="goToStep4"
                >
                  {{
                    step2Saving
                      ? "Saving Address..."
                      : "Continue to Bank Details"
                  }}
                  <i class="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>

            <!-- ── STEP 4: Bank Details ── -->
            <div v-if="distStep === 4" class="wizard-step-content">
              <div class="step-header">
                <h3 class="step-title">Bank Account Details</h3>
                <p class="step-subtitle">
                  Required for payouts. You can complete this within 30 days of
                  registration.
                </p>
              </div>

              <div v-if="!bankAccountVerified" class="form-grid">
                <div class="form-group">
                  <label>Account Number <span class="required">*</span></label>
                  <input
                    v-model="dist.bankAccount"
                    type="text"
                    placeholder="Enter account number"
                  />
                </div>
                <div class="form-group">
                  <label
                    >Confirm Account Number
                    <span class="required">*</span></label
                  >
                  <input
                    v-model="dist.bankAccountConfirm"
                    type="text"
                    placeholder="Re-enter account number"
                  />
                </div>
                <div class="form-group">
                  <label>IFSC Code <span class="required">*</span></label>
                  <input
                    v-model="dist.ifsc"
                    type="text"
                    placeholder="e.g. SBIN0001234"
                    maxlength="11"
                    @input="dist.ifsc = dist.ifsc.toUpperCase()"
                  />
                </div>
                <div class="form-group" style="margin-top: 24px">
                  <button
                    type="button"
                    class="next-btn"
                    :disabled="step3Saving"
                    @click="verifyBankAccount"
                  >
                    {{
                      step3Saving ? "Verifying Bank..." : "Verify Bank Account"
                    }}
                    <i class="ri-arrow-right-line"></i>
                  </button>
                </div>
              </div>

              <div v-else class="verified-info-box" style="margin-top: 8px">
                <i class="ri-checkbox-circle-fill"></i>
                <div>
                  <p class="verified-info-name">Bank Account Verified</p>
                </div>
              </div>

              <p v-if="step3Error" class="step-error">{{ step3Error }}</p>

              <div class="step-actions two-col">
                <button type="button" class="back-btn" @click="distStep = 3">
                  <i class="ri-arrow-left-line"></i> Back
                </button>
                <div class="right-actions">
                  <button
                    v-if="!bankAccountVerified"
                    type="button"
                    class="do-later-btn"
                    @click="showBankSkipDialog = true"
                  >
                    Do it later
                  </button>
                  <button
                    type="button"
                    class="next-btn"
                    :disabled="!bankAccountVerified"
                    @click="goToStep5"
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>

            <!-- ── STEP 5: Review & Submit ── -->
            <div v-if="distStep === 5" class="wizard-step-content">
              <div class="step-header">
                <h3 class="step-title">Review & Submit</h3>
                <p class="step-subtitle">
                  Confirm your details and complete the Distributor Agreement to
                  finish registration.
                </p>
              </div>

              <div class="summary-grid">
                <!-- Identity Card -->
                <div class="summary-card">
                  <div class="summary-card-header">
                    <i class="ri-user-line"></i>
                    <span>Identity</span>
                  </div>
                  <div class="summary-row">
                    <span>Name</span><strong>{{ dist.fullName || "—" }}</strong>
                  </div>
                  <div class="summary-row">
                    <span>Mobile</span><strong>{{ dist.mobile || "—" }}</strong>
                  </div>
                  <div class="summary-row">
                    <span>Email</span><strong>{{ dist.email || "—" }}</strong>
                  </div>
                  <div class="summary-row">
                    <span>Date of Birth</span
                    ><strong>{{ dist.dob || "—" }}</strong>
                  </div>
                  <div
                    v-if="!bankSkipped && dist.bankAccountHolder"
                    class="summary-row"
                  >
                    <span>Bank Account Holder</span
                    ><strong>{{ dist.bankAccountHolder }}</strong>
                  </div>
                  <div
                    v-if="getAadhaarAddressText(aadhaarAddress)"
                    class="summary-row address-row"
                  >
                    <span>Billing Address</span>
                    <strong>{{
                      saveAsBilling
                        ? getAadhaarAddressText(aadhaarAddress)
                        : "Not saved"
                    }}</strong>
                  </div>
                </div>

                <!-- Verification Status Card -->
                <div class="summary-card">
                  <div class="summary-card-header">
                    <i class="ri-shield-check-line"></i>
                    <span>Verification Status</span>
                  </div>
                  <div class="summary-row">
                    <span>Mobile</span>
                    <strong
                      :class="mobileVerified ? 'status-ok' : 'status-pending'"
                    >
                      <i
                        :class="
                          mobileVerified
                            ? 'ri-checkbox-circle-fill'
                            : 'ri-time-line'
                        "
                      ></i>
                      {{ mobileVerified ? "Verified" : "Pending" }}
                    </strong>
                  </div>
                  <div class="summary-row">
                    <span>Aadhaar</span>
                    <strong
                      :class="aadhaarVerified ? 'status-ok' : 'status-pending'"
                    >
                      <i
                        :class="
                          aadhaarVerified
                            ? 'ri-checkbox-circle-fill'
                            : 'ri-time-line'
                        "
                      ></i>
                      {{ aadhaarVerified ? "Verified" : "Pending" }}
                    </strong>
                  </div>
                  <div class="summary-row">
                    <span>PAN</span>
                    <strong
                      :class="panVerified ? 'status-ok' : 'status-pending'"
                    >
                      <i
                        :class="
                          panVerified
                            ? 'ri-checkbox-circle-fill'
                            : 'ri-time-line'
                        "
                      ></i>
                      {{ panVerified ? "Verified" : "Pending" }}
                    </strong>
                  </div>
                  <div class="summary-row">
                    <span>Bank Account</span>
                    <strong :class="!bankSkipped ? 'status-ok' : 'status-warn'">
                      <i
                        :class="
                          !bankSkipped
                            ? 'ri-checkbox-circle-fill'
                            : 'ri-time-line'
                        "
                      ></i>
                      {{ !bankSkipped ? "Verified" : "Pending (30 days)" }}
                    </strong>
                  </div>
                </div>
              </div>

              <!-- ═══════ DISTRIBUTOR AGREEMENT & CONSENT (4 phases) ═══════ -->
              <div class="tnc-box">
                <h4 class="tnc-title">Distributor Agreement &amp; Consent</h4>

                <!-- PHASE 1: Mandatory Compliance Checkboxes -->
                <div class="tnc-phase">
                  <p class="tnc-phase-label">
                    <span class="tnc-phase-number">1</span>
                    {{ phase1TitleText }}
                  </p>

                  <div class="tnc-checklist">
                    <div
                      v-for="(item, idx) in phase1Items"
                      :key="idx"
                      class="tnc-checklist-item"
                      :class="{ accepted: phase1Checks[idx] }"
                    >
                      <div class="tnc-consent-control">
                        <input
                          v-if="isConsentCheckbox(item)"
                          type="checkbox"
                          v-model="phase1Checks[idx]"
                        />
                        <button
                          v-else
                          type="button"
                          class="consent-accept-btn"
                          :disabled="phase1Checks[idx]"
                          @click="acceptConsentByButton(idx, item)"
                        >
                          {{
                            phase1Checks[idx]
                              ? "Accepted"
                              : resolveConsentButtonText(item)
                          }}
                        </button>
                      </div>
                      <span class="tnc-checklist-text">
                        <strong>
                          <span class="tnc-serial"
                            >{{ item.displayOrder }}.</span
                          >
                          {{ item.title }}
                        </strong>
                        <em>&ldquo;{{ item.text }}&rdquo;</em>
                      </span>
                    </div>
                  </div>
                </div>

                <!-- PHASE 2: Solemn Declaration (scroll gate + Agree button) -->
                <transition name="slide-down">
                  <div v-if="allPhase1Checked" class="tnc-phase">
                    <p class="tnc-phase-label">
                      <span class="tnc-phase-number">2</span>
                      {{ phase2TitleText }}
                    </p>

                    <div
                      class="tnc-scroll"
                      ref="tncScrollRef"
                      @scroll="handleTncScroll"
                    >
                      <template v-if="phase2AgreementText">
                        <p class="tnc-dynamic-agreement">
                          {{ phase2AgreementText }}
                        </p>
                      </template>
                      <template v-else>
                        <p class="tnc-declaration-heading">
                          SOLEMN DECLARATION AND AGREEMENT
                        </p>
                        <p>
                          By registering as an Independent Distributor with
                          <strong>INDIEKONNECT ENTERPRISES PVT LTD</strong>, I
                          solemnly declare, undertake, and agree to the
                          following terms and conditions:
                        </p>

                        <p class="tnc-section-heading">
                          Section I: Presentation &amp; Ethical Marketing
                          Guidelines
                        </p>
                        <ol>
                          <li>
                            When presenting INDIEKONNECT's products, services,
                            and business opportunities to prospective customers,
                            I shall strictly adhere to the following
                            professional guidelines:
                            <ol type="a">
                              <li>
                                Respect and adhere to all local, state, and
                                central laws of India.
                              </li>
                              <li>
                                Provide strictly accurate and verified
                                information regarding products, services, sales
                                methods, and the Compensation Plan.
                              </li>
                              <li>
                                Avoid any inaccurate, unfair, or unethical
                                comparisons of products or opportunities.
                              </li>
                              <li>
                                Refrain from any unethical, unlawful, or
                                coercive recruitment practices.
                              </li>
                              <li>
                                Accurately present my relationship as an
                                independent entrepreneur and not an employee or
                                agent of the company.
                              </li>
                              <li>
                                Clarify that commissions are solely based on
                                hard work, ethical marketing, and actual product
                                purchases, not on recruitment.
                              </li>
                              <li>
                                Abide by the Code of Ethics and convey these
                                principles to all customers.
                              </li>
                            </ol>
                          </li>
                          <li>
                            I understand that I am fully and solely responsible
                            for all content, information, and claims I share
                            during any presentation. I will not hold
                            INDIEKONNECT or its affiliates liable for any
                            unauthorized claims, unethical practices, or
                            representations made by me.
                          </li>
                        </ol>

                        <p class="tnc-section-heading">
                          Section II: Independent Status, Taxes &amp; Expenses
                        </p>
                        <ol>
                          <li>
                            I am an independent, self-employed entrepreneur and
                            not an employee, partner, or joint-venture associate
                            of INDIEKONNECT. I am solely responsible for all
                            personal business expenses related to my marketing
                            activities.
                          </li>
                          <li>
                            I am solely responsible for complying with all
                            applicable tax laws, including Income Tax, GST,
                            Professional Tax, local levies, and statutory
                            registrations required to operate my business.
                          </li>
                          <li>
                            I agree that TDS (Tax Deducted at Source) will be
                            automatically deducted by the company from any
                            commissions payable to me in accordance with the
                            Income Tax Act.
                          </li>
                          <li>
                            I understand that in the event of my resignation or
                            termination due to non-compliance, I forfeit all
                            rights to commissions, back-office access, and
                            further product purchases.
                          </li>
                          <li>
                            This agreement will automatically terminate in the
                            event of no business activity for a consecutive
                            period of 24 months.
                          </li>
                        </ol>

                        <p class="tnc-section-heading">
                          Section III: Rules of Communication &amp; Fair
                          Competition
                        </p>
                        <ol>
                          <li>
                            I shall communicate and represent the products and
                            services strictly in accordance with official
                            company training and authorized publications. I will
                            never propagate unauthorized, misleading, or
                            exaggerated product or earning claims.
                          </li>
                          <li>
                            I will not unfairly denigrate, criticize, or make
                            negative comparisons regarding any other brand,
                            organization, or product, directly or indirectly.
                          </li>
                          <li>
                            I undertake to verify the accuracy of all company
                            information using the official website
                            (www.indiekonnect.com) before disseminating it.
                          </li>
                        </ol>

                        <p class="tnc-section-heading">
                          Section IV: Regulatory Compliance, Grievance Redressal
                          &amp; Data Consent
                        </p>
                        <ol>
                          <li>
                            I declare that I am not engaged, and will not
                            engage, in any anti-national, illegal, or restricted
                            activities.
                          </li>
                          <li>
                            I understand that system upgrades and process
                            changes are ongoing, and I can seek clarification by
                            writing to customercare@indiekonnect.com.
                          </li>
                          <li>
                            I agree that the company reserves the absolute right
                            to reject my distributor application at its sole
                            discretion, without providing any reasons.
                          </li>
                          <li>
                            <strong>Grievance Redressal:</strong> In the event
                            of any complaint or dispute, I agree to first submit
                            it to grievance@indiekonnect.com and allow the
                            company a 45-day window to provide an unbiased
                            resolution before escalating the matter to the
                            Department of Consumer Affairs or any other
                            governmental body.
                          </li>
                          <li>
                            I agree to receive transactional and promotional
                            updates from the company via my registered email
                            address and mobile number.
                          </li>
                          <li>
                            I consent to my personal registration details and
                            business data being shared with government and
                            regulatory agencies if required by law.
                          </li>
                        </ol>
                      </template>
                    </div>

                    <p
                      v-if="!tncScrolledToEnd"
                      class="field-hint tnc-scroll-hint"
                    >
                      <i class="ri-arrow-down-double-line"></i>
                      Please scroll to the end of the agreement to continue.
                    </p>

                    <template v-else>
                      <p v-if="!phase2Agreed" class="tnc-scroll-complete">
                        <i class="ri-checkbox-circle-fill"></i>
                        You've read the full Distributor Agreement.
                      </p>

                      <div
                        v-if="!phase2Agreed"
                        class="step-actions"
                        style="margin-top: 14px"
                      >
                        <button
                          type="button"
                          class="next-btn"
                          @click="agreeToPhase2"
                        >
                          <i class="ri-checkbox-circle-line"></i>
                          {{ phase2ButtonText }}
                        </button>
                      </div>

                      <p v-else class="tnc-scroll-complete">
                        <i class="ri-checkbox-circle-fill"></i>
                        You've agreed to the Distributor Agreement.
                      </p>
                    </template>
                  </div>
                </transition>

                <!-- PHASE 3: Type-to-Consent -->
                <transition name="slide-down">
                  <div
                    v-if="allPhase1Checked && phase2Agreed"
                    class="tnc-phase"
                  >
                    <p class="tnc-phase-label">
                      <span class="tnc-phase-number">3</span>
                      {{ phase3TitleText }}
                    </p>

                    <p class="declaration-instruction">
                      To confirm that you've read the guidelines and all clauses
                      of the Joining Agreement above, type the phrase below
                      exactly as written in the box provided.
                    </p>

                    <div class="declaration-expected-box">
                      {{ expectedDeclaration }}
                    </div>

                    <textarea
                      v-model="declaration"
                      rows="3"
                      class="declaration-textarea"
                      placeholder="Type the phrase here (pasting is disabled)..."
                      @paste.prevent
                      @drop.prevent
                      @dragover.prevent
                      @contextmenu.prevent
                    ></textarea>
                    <p class="field-hint" style="margin-top: 6px">
                      <i class="ri-information-line"></i> Pasting is disabled
                      for this field — please type the phrase manually.
                    </p>
                    <p v-if="declarationMismatch" class="field-error">
                      The text you entered does not match. Please type the exact
                      phrase shown above (casing doesn't matter).
                    </p>
                    <p
                      v-else-if="declaration.trim() && declarationConfirmed"
                      class="tnc-scroll-complete"
                    >
                      <i class="ri-checkbox-circle-fill"></i> Confirmation
                      phrase matched.
                    </p>
                  </div>
                </transition>

                <!-- PHASE 4: Electronic Signature & Verification -->
                <transition name="slide-down">
                  <div
                    v-if="
                      allPhase1Checked && phase2Agreed && declarationConfirmed
                    "
                    class="tnc-phase"
                  >
                    <p class="tnc-phase-label">
                      <span class="tnc-phase-number">4</span>
                      {{ phase4TitleText }}
                    </p>

                    <div class="form-grid">
                      <div class="form-group">
                        <label
                          >{{ signatureNameLabel }}
                          <span class="required">*</span></label
                        >
                        <input
                          v-model="signatureFullName"
                          type="text"
                          placeholder="e.g. Rohan Sharma"
                          :class="{
                            'input-verified':
                              signatureFullName.trim() && signatureNameValid,
                            'input-error':
                              signatureFullName.trim() && !signatureNameValid,
                          }"
                        />
                        <!-- <p
                          v-if="signatureFullName.trim() && !signatureNameValid"
                          class="field-error"
                        >
                          Use letters only, with exactly one space between words
                          (no leading, trailing, or double spaces).
                        </p> -->
                      </div>
                      <div class="form-group">
                        <label
                          >{{ signaturePanLabel }}
                          <span class="required">*</span></label
                        >
                        <input
                          v-model="signaturePan"
                          type="text"
                          placeholder="ABCDE1234F"
                          maxlength="10"
                          @input="signaturePan = signaturePan.toUpperCase()"
                          :class="{
                            'input-verified':
                              signaturePan.trim() && signaturePanValid,
                            'input-error':
                              signaturePan.trim() && !signaturePanValid,
                          }"
                        />
                        <p
                          v-if="signaturePan.trim() && !signaturePanValid"
                          class="field-error"
                        >
                          Enter a valid PAN in the format ABCDE1234F.
                        </p>
                      </div>
                    </div>

                    <!-- Date & Time of signing only appears once name + PAN
                         are both filled in and pass validation (stand-in for
                         "verified" until real verification is wired up). -->
                    <p
                      v-if="signatureNameValid && signaturePanValid"
                      class="field-hint"
                      style="margin-top: 12px"
                    >
                      Date of Signing: <strong>{{ dateOfSigning }}</strong> at
                      <strong>{{ timeOfSigning }}</strong>
                    </p>
                  </div>
                </transition>
              </div>

              <p v-if="step4Error" class="step-error">{{ step4Error }}</p>

              <div class="step-actions two-col">
                <button type="button" class="back-btn" @click="distStep = 4">
                  <i class="ri-arrow-left-line"></i> Back
                </button>
                <button
                  type="button"
                  class="submit-btn"
                  :disabled="!isTncFullyComplete || isSubmitting"
                  @click="handleDistributorSubmit"
                >
                  <i class="ri-send-plane-line"></i>
                  {{ isSubmitting ? "Submitting..." : "Submit Registration" }}
                </button>
              </div>
            </div>

            <!-- ── SUCCESS STATE ── -->
            <div v-if="distStep === 6" class="success-state">
              <div class="success-icon">
                <i class="ri-checkbox-circle-fill"></i>
              </div>
              <h3>Registration Submitted!</h3>
              <p>
                Your distributor application has been received. Our team will
                review and activate your account within 24–48 hours. You'll
                receive a confirmation on <strong>{{ dist.mobile }}</strong>
                <template v-if="dist.email">
                  and <strong>{{ dist.email }}</strong></template
                >.
              </p>

              <div class="success-cards-row">
                <div class="success-summary-card">
                  <div class="success-card-title">
                    <i class="ri-shield-check-line"></i> Verification
                  </div>
                  <div
                    class="success-check-item"
                    :class="{ ok: mobileVerified }"
                  >
                    <i
                      :class="
                        mobileVerified
                          ? 'ri-checkbox-circle-fill'
                          : 'ri-checkbox-blank-circle-line'
                      "
                    ></i>
                    Mobile Verified
                  </div>
                  <div
                    class="success-check-item"
                    :class="{ ok: aadhaarVerified }"
                  >
                    <i
                      :class="
                        aadhaarVerified
                          ? 'ri-checkbox-circle-fill'
                          : 'ri-checkbox-blank-circle-line'
                      "
                    ></i>
                    Aadhaar Verified
                  </div>
                  <div class="success-check-item" :class="{ ok: panVerified }">
                    <i
                      :class="
                        panVerified
                          ? 'ri-checkbox-circle-fill'
                          : 'ri-checkbox-blank-circle-line'
                      "
                    ></i>
                    PAN Verified
                  </div>
                  <div class="success-check-item ok">
                    <i class="ri-checkbox-circle-fill"></i> Consent Provided
                  </div>
                  <div v-if="!bankSkipped" class="success-check-item ok">
                    <i class="ri-checkbox-circle-fill"></i> Bank Account
                    Verified
                  </div>
                  <div
                    v-if="bankSkipped"
                    class="success-check-item bank-pending-item"
                  >
                    <i class="ri-time-line"></i> Bank Account — Complete within
                    30 days
                  </div>
                </div>

                <div class="success-summary-card">
                  <div class="success-card-title">
                    <i class="ri-user-star-line"></i> BA Account
                  </div>
                  <div class="success-check-item ok">
                    <i class="ri-checkbox-circle-fill"></i>
                    BA ID:
                    <strong style="margin-left: 4px">
                      {{ registeredBaId || "N/A" }}</strong
                    >
                  </div>
                  <p class="ba-note">
                    <i class="ri-information-line"></i>
                    Complete your first purchase to activate BA pricing.
                  </p>
                </div>
              </div>

              <!-- Auto-login status notice (only shown if silent auto-login after
                   registration did not succeed) -->
              <p
                v-if="step4Error"
                class="field-hint"
                style="
                  margin: 0 0 16px;
                  color: #d97706;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 6px;
                "
              >
                <i class="ri-information-line"></i> {{ step4Error }}
              </p>
              <p
                v-if="shopNowError"
                class="field-hint"
                style="
                  margin: 0 0 16px;
                  color: #d97706;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 6px;
                "
              >
                <i class="ri-information-line"></i> {{ shopNowError }}
              </p>

              <button
                type="button"
                class="shop-now-btn"
                @click="handleShopNowClick"
              >
                <!-- disabled -->
                <i class="ri-shopping-bag-line"></i> Shop Now
              </button>
              <button
                v-if="!isRegisteredImported"
                type="button"
                class="shop-now-btn placement-btn"
                @click="handlePlacementClick"
              >
                <i class="ri-route-line"></i> Placement
              </button>
            </div>
          </template>

          <!-- <div
            v-if="selectedRole !== 'customer' && toastMessage"
            :class="['toast', toastType]"
          >
            {{ toastMessage }}
          </div> -->
        </div>
      </div>
    </section>

    <div
      v-if="showPlacementPopup"
      class="placement-popup-overlay"
      @click.self="showPlacementPopup = false"
    >
      <div class="placement-popup-box">
        <button
          type="button"
          class="placement-popup-close"
          @click="showPlacementPopup = false"
        >
          <i class="ri-close-line"></i>
        </button>
        <iframe
          class="placement-popup-iframe"
          :src="placementUrl"
          title="Placement"
        ></iframe>
      </div>
    </div>

    <!-- BANK SKIP WARNING DIALOG -->
    <div
      v-if="showBankSkipDialog"
      class="dialog-overlay"
      @click.self="showBankSkipDialog = false"
    >
      <div class="dialog-box">
        <div class="dialog-icon"><i class="ri-error-warning-line"></i></div>
        <h4 class="dialog-title">Are you sure?</h4>
        <p class="dialog-body">
          Bank account verification is
          <strong>mandatory within 30 days</strong> of registration. Unverified
          accounts will not be eligible to receive payouts. You can complete
          this anytime from your dashboard.
        </p>
        <div class="dialog-actions">
          <button
            type="button"
            class="dialog-cancel-btn"
            @click="showBankSkipDialog = false"
          >
            Go Back
          </button>
          <button type="button" class="dialog-ok-btn" @click="confirmSkipBank">
            Yes, I'll do it later
          </button>
        </div>
      </div>
    </div>

    <!-- AADHAAR BACK CONFIRMATION DIALOG -->
    <div v-if="showAadhaarBackDialog" class="dialog-overlay">
      <div class="dialog-box">
        <div class="dialog-icon"><i class="ri-error-warning-line"></i></div>
        <h4 class="dialog-title">Proceed with Back?</h4>
        <p class="dialog-body">
          Your Aadhaar has been verified. Going back will clear this
          verification. Do you want to proceed anyway?
        </p>
        <div class="dialog-actions">
          <button
            type="button"
            class="dialog-cancel-btn"
            @click="cancelKycStepBack"
          >
            No
          </button>
          <button
            type="button"
            class="dialog-ok-btn"
            @click="confirmKycStepBack"
          >
            Yes
          </button>
        </div>
      </div>
    </div>

    <div v-if="showPanTypeSwitchDialog" class="dialog-overlay">
      <div class="dialog-box">
        <div class="dialog-icon"><i class="ri-error-warning-line"></i></div>
        <h4 class="dialog-title">Are you sure?</h4>
        <p class="dialog-body">
          You're switching the PAN type to
          <strong>{{
            pendingPanApplicantType === "huf" ? "HUF" : "Individual"
          }}</strong
          >. Any PAN details you've entered will be cleared and you'll need to
          enter them again. Do you want to proceed?
        </p>
        <div class="dialog-actions">
          <button
            type="button"
            class="dialog-cancel-btn"
            @click="cancelPanApplicantTypeSwitch"
          >
            No
          </button>
          <button
            type="button"
            class="dialog-ok-btn"
            @click="confirmPanApplicantTypeSwitch"
          >
            Yes
          </button>
        </div>
      </div>
    </div>

    <div v-if="isDigilockerFlowBlocking" class="digilocker-blocking-overlay">
      <div class="digilocker-blocking-card">
        <i class="ri-loader-4-line spinner"></i>
        <p>
          {{
            digilockerStatusChecking
              ? "Checking digiLocker verification status..."
              : "DigiLocker verification initiated. Please complete to continue..."
          }}
        </p>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import AppNavbar from "./AppNavbar.vue";
import { useAuthStore } from "../stores/useAuthStore.js";
import Footer from "./FooterView.vue";
import {
  register,
  validateRegisterOtp,
  getToken,
  setToken,
  sendRegisterVerifyEmailOtp,
  validateRegisterVerifyEmailOtp,
  sendRegisterVerifyMobileOtp,
  validateRegisterVerifyMobileOtp,
  validateRegisterReference,
  saveRegisterDetails,
  sendRegisterVerifyAadhaarOtp,
  initiateRegisterVerifyAadhaarDigilockerSession,
  checkRegisterVerifyAadhaarDigilockerSession,
  validateRegisterVerifyAadhaarOtp,
  saveRegisterAadhaarAddress,
  validateRegisterPan,
  validateRegisterBankAccount,
  getActiveRegisterConsentRequirements,
} from "rdep-ecom-sdk";

// ─── SHARED ───
const selectedRole = ref("customer");
const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const maxDob = new Date().toISOString().split("T")[0];

const DIGILOCKER_STATUS_CHECK_TIMEOUT_MS = 20000;

function normalizeRole(value) {
  return value === "distributor" ? "distributor" : "customer";
}

function syncRoleFromRoute() {
  selectedRole.value = normalizeRole(route.query.role);
}

const registrationResponseData = ref({});

const registeredBaId = computed(() => {
  return String(registrationResponseData.value?.distributorCode || "").trim();
});

const isRegisteredImported = computed(() => {
  const importedRaw =
    registrationResponseData.value?.imported ??
    registrationResponseData.value?.data?.imported ??
    latestRegisterResponse.value?.imported ??
    latestRegisterResponse.value?.data?.imported ??
    false;

  return (
    importedRaw === true ||
    importedRaw === 1 ||
    String(importedRaw || "").toLowerCase() === "true"
  );
});

watch(
  () => route.query.role,
  () => {
    syncRoleFromRoute();
  },
  { immediate: true },
);

// ─── CUSTOMER STATE ───
const firstName = ref("");
const email = ref("");
const mobileNumber = ref("");
const password = ref("");
const showPassword = ref(false);
const isLoading = ref(false);
const isOtpLoading = ref(false);
const isResendOtpLoading = ref(false);
const toastMessage = ref("");
const toastType = ref("success");
const customerOtpRequired = ref(false);
const customerOtp = ref("");
const customerRegisterTransactionId = ref("");

function togglePasswordVisibility() {
  showPassword.value = !showPassword.value;
}

function showToast(message, type) {
  toastMessage.value = message;
  toastType.value = type;
  setTimeout(() => {
    toastMessage.value = "";
  }, 3000);
}

async function handleRegister() {
  if (customerOtpRequired.value) {
    return;
  }

  toastMessage.value = "";
  const domainName = getDomainNameForApi();
  const cleanFullName = String(firstName.value || "").trim();
  const cleanEmail = String(email.value || "").trim();
  const cleanMobile = String(mobileNumber.value || "").replace(/\D/g, "");
  const cleanPassword = String(password.value || "");
  const nameParts = cleanFullName.split(/\s+/).filter(Boolean);
  const cleanFirstName = String(nameParts[0] || "").trim();
  const cleanLastName = String(nameParts.slice(1).join(" ") || "").trim();

  if (!cleanFullName || !cleanEmail || !cleanMobile || !cleanPassword) {
    showToast("Please fill all required fields.", "error");
    return;
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(cleanEmail)) {
    showToast("Please enter a valid email address.", "error");
    return;
  }

  if (cleanMobile.length !== 10) {
    showToast("Please enter a valid 10-digit mobile number.", "error");
    return;
  }

  if (cleanPassword.length < 6) {
    showToast("Password must be at least 6 characters.", "error");
    return;
  }

  isLoading.value = true;
  try {
    const response = await register({
      firstName: cleanFirstName,
      middleName: "",
      lastName: cleanLastName,
      email: cleanEmail,
      mobileNumber: cleanMobile,
      password: cleanPassword,
      domainName,
    });

    const resolvedTransactionId = String(
      response?.transactionId || response?.data?.transactionId || "",
    ).trim();

    const statusCode = String(
      response?.statusCode ?? response?.data?.statusCode ?? "",
    );
    const statusMessage =
      response?.statusMessage ?? response?.data?.statusMessage;
    if (statusCode === "200" && resolvedTransactionId) {
      customerRegisterTransactionId.value = resolvedTransactionId;
      customerOtpRequired.value = true;
      customerOtp.value = "";
      showToast(
        statusMessage || "OTP sent. Please enter OTP to complete registration.",
        "success",
      );
    } else {
      const toastVariant = statusCode === "400" ? "warning" : "error";
      showToast(
        statusMessage ||
          "Unable to create account. Transaction ID missing for OTP verification.",
        toastVariant,
      );
    }
  } catch (error) {
    const errorStatusCode = String(
      error?.response?.data?.statusCode ?? error?.response?.status ?? "",
    );
    const errorStatusMessage =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Unable to create account. Please try again.";
    showToast(
      errorStatusMessage,
      errorStatusCode === "400" ? "warning" : "error",
    );
  } finally {
    isLoading.value = false;
  }
}

async function handleCustomerOtpConfirm() {
  toastMessage.value = "";

  const domainName = getDomainNameForApi();
  const cleanFullName = String(firstName.value || "").trim();
  const cleanEmail = String(email.value || "").trim();
  const cleanMobile = String(mobileNumber.value || "").replace(/\D/g, "");
  const cleanPassword = String(password.value || "");
  const cleanOtp = String(customerOtp.value || "")
    .replace(/\D/g, "")
    .trim();
  const transactionId = String(
    customerRegisterTransactionId.value || "",
  ).trim();
  const nameParts = cleanFullName.split(/\s+/).filter(Boolean);
  const cleanFirstName = String(nameParts[0] || "").trim();
  const cleanLastName = String(nameParts.slice(1).join(" ") || "").trim();

  if (!transactionId) {
    showToast("Transaction ID is missing. Please register again.", "error");
    return;
  }

  if (cleanOtp.length < 4) {
    showToast("Please enter a valid OTP.", "error");
    return;
  }

  isOtpLoading.value = true;
  try {
    const response = await validateRegisterOtp({
      firstName: cleanFirstName,
      middleName: "",
      lastName: cleanLastName,
      email: cleanEmail,
      mobileNumber: cleanMobile,
      password: cleanPassword,
      domainName,
      transactionId,
      otp: cleanOtp,
    });

    const statusCode = String(
      response?.statusCode ?? response?.data?.statusCode ?? "",
    );
    const statusMessage =
      response?.statusMessage ??
      response?.data?.statusMessage ??
      "OTP verified successfully.";

    if (statusCode === "200") {
      showToast(statusMessage, "success");
      customerOtpRequired.value = false;
      customerOtp.value = "";
      customerRegisterTransactionId.value = "";
      setTimeout(() => {
        router.push({
          name: "login",
          query: {
            role: "customer",
          },
        });
      }, 1200);
      return;
    }

    showToast(statusMessage || "OTP verification failed.", "error");
  } catch (error) {
    const message =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "OTP verification failed. Please try again.";
    showToast(message, "error");
  } finally {
    isOtpLoading.value = false;
  }
}

async function handleCustomerOtpResend() {
  toastMessage.value = "";

  if (!customerOtpRequired.value) {
    showToast("Please create account first.", "error");
    return;
  }

  const domainName = getDomainNameForApi();
  const cleanFullName = String(firstName.value || "").trim();
  const cleanEmail = String(email.value || "").trim();
  const cleanMobile = String(mobileNumber.value || "").replace(/\D/g, "");
  const cleanPassword = String(password.value || "");
  const nameParts = cleanFullName.split(/\s+/).filter(Boolean);
  const cleanFirstName = String(nameParts[0] || "").trim();
  const cleanLastName = String(nameParts.slice(1).join(" ") || "").trim();

  if (
    !cleanFullName ||
    !cleanEmail ||
    cleanMobile.length !== 10 ||
    !cleanPassword
  ) {
    showToast(
      "Valid full name, email, mobile number, and password are required.",
      "error",
    );
    return;
  }

  isResendOtpLoading.value = true;
  try {
    const response = await register({
      firstName: cleanFirstName,
      middleName: "",
      lastName: cleanLastName,
      email: cleanEmail,
      mobileNumber: cleanMobile,
      password: cleanPassword,
      domainName,
    });

    const statusCode = String(
      response?.statusCode ?? response?.data?.statusCode ?? "",
    );
    const statusMessage =
      response?.statusMessage ||
      response?.data?.statusMessage ||
      "OTP resent successfully.";

    const refreshedTransactionId = String(
      response?.transactionId || response?.data?.transactionId || "",
    ).trim();

    if (statusCode === "200") {
      if (refreshedTransactionId) {
        customerRegisterTransactionId.value = refreshedTransactionId;
      }
      customerOtpRequired.value = true;
      customerOtp.value = "";
      showToast(statusMessage, "success");
      return;
    }

    showToast(statusMessage || "Unable to resend OTP.", "error");
  } catch (error) {
    const message =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Unable to resend OTP. Please try again.";
    showToast(message, "error");
  } finally {
    isResendOtpLoading.value = false;
  }
}

// ─── DISTRIBUTOR WIZARD STATE ───
const distStep = ref(1);
const isSubmitting = ref(false);
const showDistPassword = ref(false);
const showDistConfirmPassword = ref(false);
const bankSkipped = ref(false);
const declaration = ref("");
const showBankSkipDialog = ref(false);
const showAadhaarBackDialog = ref(false);
const showPanTypeSwitchDialog = ref(false);
const panApplicantType = ref("individual");
const pendingPanApplicantType = ref("individual");
const latestRegisterResponse = ref(null);
const latestRegisterRawResponse = ref(null);
const shopNowError = ref("");
const showPlacementPopup = ref(false);
const placementUrl = ref("");
const consentDocuments = ref([]);
const consentHandshakeToken = ref("");

// ─── DISTRIBUTOR AGREEMENT — PHASE 1: compliance checkboxes ───
const defaultPhase1Items = [
  {
    displayOrder: 1,
    serial: 1,
    acceptanceType: "CHECKBOX",
    acceptedValue: "CHECKED",
    title: "Eligibility, Orientation & Free Will (Clauses 1, 2, 8, 18):",
    text: "I confirm that I am a citizen of India, at least 18 years of age, and legally authorized to enter into this contract. I have undergone the mandatory company orientation session (online or offline), had all my questions answered to my satisfaction, and have been treated fairly by my sponsor. I register willingly, of my own free choice, without any coercion, pressure, or false promises of guaranteed income.",
  },
  {
    displayOrder: 2,
    serial: 2,
    acceptanceType: "CHECKBOX",
    acceptedValue: "CHECKED",
    title: "No Enrollment Fee & Direct Selling Model (Clauses 9, 10, 13):",
    text: "I understand that there is no fee for enrolling as a Distributor, and there is no compulsory purchase required to join. I understand that my earnings are solely based on product sales according to the official Compensation Plan, and not on recruitment. Any product purchase I make is for personal use and does not guarantee commissions.",
  },
  {
    displayOrder: 3,
    serial: 3,
    acceptanceType: "CHECKBOX",
    acceptedValue: "CHECKED",
    title: "Accuracy of KYC & Identity Documents (Clauses 4, 23, 24):",
    text: "I certify that all details provided during registration are true, accurate, and do not use false or fictitious names. I agree to upload self-attested, valid online KYC documents (PAN Card, Address Proof, Signature Proof, and ID Proof). I acknowledge that the Company has the right to block my back-office access and withhold incentives until my KYC is verified, and that providing false details may lead to termination and legal action.",
  },
  {
    displayOrder: 4,
    serial: 4,
    acceptanceType: "CHECKBOX",
    acceptedValue: "CHECKED",
    title: "Consent to Company Materials & Policies (Clauses 3, 19, 21, 25):",
    text: "I declare that I have read, understood, and agreed to be legally bound by the Company Materials, which include the Distributor Agreement, Code of Ethics, Policies & Procedures, and the Compensation Plan (all available on the official website). I agree to abide by future amendments to these documents, and I understand that if I disagree with them, I am free to resign.",
  },
  {
    displayOrder: 5,
    serial: 5,
    acceptanceType: "CHECKBOX",
    acceptedValue: "CHECKED",
    title: "30-Day Buy-Back / Product Refund Policy (Clause 5):",
    text: "I understand and agree that the company offers a 30-Day Buy-Back Policy (calculated from the product invoice date) for marketable inventory. I undertake to always inform my prospective customers about this policy and its relevant conditions.",
  },
];

const phase1Items = ref([...defaultPhase1Items]);
const phase1Checks = ref(phase1Items.value.map(() => false));
const phase2ConsentDocument = ref(null);
const phase2AgreementText = ref("");
const phase3ConsentDocument = ref(null);
const phase4ConsentDocument = ref(null);

const phase1TitleText = computed(() => "Read and confirm each statement below");
const phase2TitleText = computed(() => {
  const title = String(
    phase2ConsentDocument.value?.title ||
      "Read the full Distributor Agreement and scroll to the end",
  ).trim();
  const order = Number(phase2ConsentDocument.value?.displayOrder);
  return Number.isFinite(order) ? `${order}. ${title}` : title;
});
const phase3TitleText = computed(() => {
  const title = String(
    phase3ConsentDocument.value?.title ||
      "Type the confirmation phrase exactly as shown",
  ).trim();
  const order = Number(phase3ConsentDocument.value?.displayOrder);
  return Number.isFinite(order) ? `${order}. ${title}` : title;
});
const phase4TitleText = computed(() => {
  const title = String(
    phase4ConsentDocument.value?.title || "Electronic Signature & Verification",
  ).trim();
  const order = Number(phase4ConsentDocument.value?.displayOrder);
  return Number.isFinite(order) ? `${order}. ${title}` : title;
});

const signatureNameLabel = computed(() => {
  const labels = getSignatureLabels(phase4ConsentDocument.value);
  return labels.name;
});

const signaturePanLabel = computed(() => {
  const labels = getSignatureLabels(phase4ConsentDocument.value);
  return labels.pan;
});

const allPhase1Checked = computed(
  () =>
    phase1Checks.value.length > 0 &&
    phase1Checks.value.every((checked) => checked === true),
);

function normalizeConsentType(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function normalizeAcceptedValue(value) {
  return String(value || "")
    .trim()
    .toUpperCase();
}

function isConsentCheckbox(item) {
  const acceptanceType = normalizeConsentType(item?.acceptanceType);
  const acceptedValue = normalizeAcceptedValue(item?.acceptedValue);

  if (acceptanceType === "CHECKBOX") {
    return true;
  }

  if (acceptanceType === "BUTTON") {
    return false;
  }

  if (acceptedValue === "BUTTON") {
    return false;
  }

  return true;
}

function isConsentButton(item) {
  return !isConsentCheckbox(item);
}

function resolveConsentButtonText(item) {
  const acceptedValue = normalizeAcceptedValue(item?.acceptedValue);
  if (acceptedValue === "BUTTON") {
    return "Agree to Consent";
  }

  return "Agree";
}

function acceptConsentByButton(index, item) {
  if (!isConsentButton(item)) {
    return;
  }

  phase1Checks.value[index] = true;
}

function normalizeConsentDocument(document, index) {
  const rawDisplayOrder = Number(document?.displayOrder);
  const displayOrder = Number.isFinite(rawDisplayOrder)
    ? rawDisplayOrder
    : index + 1;
  const title = String(document?.title || "").trim();
  const text = toPlainConsentText(document?.documentHtmlContent);
  const acceptedValue = String(document?.acceptedValue || "").trim();

  return {
    consentMasterId: document?.consentMasterId,
    consentTypeCode: String(document?.consentTypeCode || "")
      .trim()
      .toUpperCase(),
    displayOrder,
    serial: displayOrder,
    documentHtmlContent: String(document?.documentHtmlContent || ""),
    contentHash: String(document?.contentHash || ""),
    acceptanceType:
      normalizeConsentType(document?.acceptanceType) || "CHECKBOX",
    acceptedValue: acceptedValue || "CHECKED",
    title: title || `Consent ${index + 1}`,
    text,
  };
}

function isPhase1ConsentDocument(item) {
  return normalizeConsentType(item?.acceptanceType) === "CHECKBOX";
}

function isPhase2ConsentDocument(item) {
  const acceptanceType = normalizeConsentType(item?.acceptanceType);
  const typeCode = String(item?.consentTypeCode || "")
    .trim()
    .toUpperCase();
  return acceptanceType === "BUTTON" || typeCode === "READ_AGREEMENT";
}

function isPhase3ConsentDocument(item) {
  const acceptanceType = normalizeConsentType(item?.acceptanceType);
  const typeCode = String(item?.consentTypeCode || "")
    .trim()
    .toUpperCase();
  return acceptanceType === "TYPE_TEXT" || typeCode === "CONFIRM_PHRASE";
}

function isPhase4ConsentDocument(item) {
  const acceptanceType = normalizeConsentType(item?.acceptanceType);
  const typeCode = String(item?.consentTypeCode || "")
    .trim()
    .toUpperCase();
  return acceptanceType === "SIGNATURE" || typeCode === "E_SIGNATURE";
}

function resolveExpectedDeclarationFromDocument(document) {
  const acceptedValue = String(document?.acceptedValue || "").trim();
  if (acceptedValue) {
    return acceptedValue;
  }

  const text = String(document?.text || "").trim();
  if (text) {
    return text;
  }

  return "I AGREE TO BE BOUND BY THE INDIEKONNECT DISTRIBUTOR AGREEMENT OF MY OWN FREE WILL.";
}

function getAcceptedValueForDocument(document) {
  const acceptanceType = normalizeConsentType(document?.acceptanceType);
  const masterId = Number(document?.consentMasterId);

  if (acceptanceType === "CHECKBOX") {
    const phaseIndex = phase1Items.value.findIndex(
      (item) => Number(item?.consentMasterId) === masterId,
    );
    return phaseIndex >= 0 && phase1Checks.value[phaseIndex] ? "CHECKED" : "";
  }

  if (acceptanceType === "BUTTON") {
    if (!phase2Agreed.value) {
      return "";
    }

    const raw = String(document?.acceptedValue || "").trim();
    const normalized = normalizeAcceptedValue(raw);
    if (!raw || normalized === "BUTTON") {
      return "ACCEPTED";
    }
    return raw;
  }

  if (acceptanceType === "TYPE_TEXT") {
    return String(expectedDeclaration.value || "").trim();
  }

  if (acceptanceType === "SIGNATURE") {
    return `${String(signatureFullName.value || "").trim()} | ${String(
      signaturePan.value || "",
    )
      .trim()
      .toUpperCase()}`;
  }

  return String(document?.acceptedValue || "").trim();
}

function buildConsentPayload() {
  const token = String(consentHandshakeToken.value || "").trim();
  if (!token) {
    return null;
  }

  const acceptances = consentDocuments.value
    .map((document) => {
      const consentMasterId = Number(document?.consentMasterId);
      const echoedHash = String(document?.contentHash || "").trim();
      const acceptedValue = getAcceptedValueForDocument(document);

      if (!Number.isFinite(consentMasterId) || !echoedHash || !acceptedValue) {
        return null;
      }

      return {
        consentMasterId,
        echoedHash,
        acceptedValue,
      };
    })
    .filter(Boolean);

  if (!acceptances.length) {
    return null;
  }

  return {
    handshakeToken: token,
    appVersion: "1",
    acceptances,
  };
}

function getSignatureLabels(document) {
  const defaultLabels = {
    name: "Full Legal Name (as on PAN / Identity Proof)",
    pan: "PAN Number (to verify your signature)",
  };

  const raw = String(document?.documentHtmlContent || "").trim();
  if (!raw) {
    return defaultLabels;
  }

  const parts = raw
    .split("|")
    .map((part) => String(part || "").trim())
    .filter(Boolean);

  if (parts.length >= 2) {
    return {
      name: parts[0],
      pan: parts[1],
    };
  }

  return defaultLabels;
}

const phase2ButtonText = computed(() => {
  const raw = String(phase2ConsentDocument.value?.acceptedValue || "").trim();
  if (!raw) {
    return "I Agree to the Distributor Agreement";
  }

  const normalized = normalizeAcceptedValue(raw);
  if (normalized === "BUTTON") {
    return "Agree to Consent";
  }

  if (normalized === "ACCEPTED") {
    return "Accept Consent";
  }

  return raw;
});

const BA_URL = String(process.env.VUE_APP_BA_URL || "https://ba-ui.qa.rdep.io");

// ─── PHASE 2: scroll-gated solemn declaration + explicit Agree button ───
const tncScrollRef = ref(null);
const tncScrolledToEnd = ref(false);
const phase2Agreed = ref(false);

function handleTncScroll(event) {
  const el = event.target;
  const threshold = 4; // px tolerance
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
    tncScrolledToEnd.value = true;
  }
}

function checkTncScrollComplete() {
  const el = tncScrollRef.value;
  if (!el) return;
  // If the content doesn't overflow (fits without scrolling), don't block the user.
  if (el.scrollHeight <= el.clientHeight + 4) {
    tncScrolledToEnd.value = true;
  }
}

function agreeToPhase2() {
  if (!tncScrolledToEnd.value) return;
  phase2Agreed.value = true;
}

// Re-check scroll completeness whenever Phase 2 becomes visible (box may
// not overflow on tall screens), and reset the gate + agreement if Phase 1
// gets unchecked.
watch(allPhase1Checked, (isChecked) => {
  if (isChecked) {
    nextTick(() => checkTncScrollComplete());
  } else {
    tncScrolledToEnd.value = false;
    phase2Agreed.value = false;
  }
});

// ─── PHASE 3: type-to-consent ───
const expectedDeclaration = ref(
  "I AGREE TO BE BOUND BY THE INDIEKONNECT DISTRIBUTOR AGREEMENT OF MY OWN FREE WILL.",
);

const declarationMismatch = computed(() => {
  if (!declaration.value.trim()) return false;
  return (
    declaration.value.trim().toLowerCase() !==
    expectedDeclaration.value.toLowerCase()
  );
});

const declarationConfirmed = computed(() => {
  return !!declaration.value.trim() && !declarationMismatch.value;
});

// ─── PHASE 4: electronic signature ───
const signatureFullName = ref("");
const signaturePan = ref("");

const PAN_FORMAT_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const signatureNameValid = computed(() =>
  Boolean(String(signatureFullName.value || "").trim()),
);

const signaturePanValid = computed(() =>
  PAN_FORMAT_REGEX.test(signaturePan.value.trim()),
);

const isKycFullyVerified = computed(
  () => aadhaarVerified.value && panVerified.value,
);

const canShowPanSection = computed(() => {
  return (
    aadhaarVerified.value &&
    (aadhaarVerifiedInCurrentFlow.value ||
      (emailImported.value && panVerified.value)) &&
    !aadhaarAddressLoading.value
  );
});

// Date/time only get read once both fields pass validation — swap the
// v-if condition in the template for a real "verified" flag later.
const dateOfSigning = computed(() => {
  return new Date().toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
});

const timeOfSigning = computed(() => {
  return new Date().toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
});

// ─── OVERALL AGREEMENT COMPLETION ───
const isTncFullyComplete = computed(() => {
  return (
    allPhase1Checked.value &&
    phase2Agreed.value &&
    declarationConfirmed.value &&
    signatureNameValid.value &&
    signaturePanValid.value
  );
});

const wizardSteps = ["Identity", "KYC", "Bank", "Review"];
const stepCompleted = ref([false, false, false, false, false]);
const stepSkipped = ref([false, false, false, false, false]);

const currentWizardStep = computed(() => {
  if (distStep.value <= 2) return 1;
  if (distStep.value === 3) return 2;
  if (distStep.value === 4) return 3;
  return 4;
});

function isWizardStepCompleted(index) {
  if (index === 0) return !!stepCompleted.value[1];
  if (index === 1) return !!stepCompleted.value[2];
  if (index === 2) return !!stepCompleted.value[3] && !stepSkipped.value[3];
  if (index === 3) return !!stepCompleted.value[4];
  return false;
}

function isWizardStepSkipped(index) {
  return index === 2 && !!stepSkipped.value[3];
}

const dist = ref({
  referrerId: "",
  fullName: "",
  mobile: "",
  dob: "",
  email: "",
  password: "",
  confirmPassword: "",
  aadhaar: "",
  hufName: "",
  hufDateOfIncorporation: "",
  pan: "",
  bankAccountHolder: "",
  bankAccount: "",
  bankAccountConfirm: "",
  ifsc: "",
});

// Email OTP
const emailVerified = ref(false);
const emailImported = ref(false);
const emailOtpSent = ref(false);
const emailOtp = ref("");
const emailError = ref("");
const emailOtpLoading = ref(false);
const emailApiResponse = ref("");
const emailValidationId = ref("");
const importedReferenceCode = ref("");

// Mobile OTP
const mobileVerified = ref(false);
const mobileOtpSent = ref(false);
const mobileOtp = ref("");
const mobileError = ref("");
const mobileOtpLoading = ref(false);
const mobileApiResponse = ref("");
const mobileValidationId = ref("");
const prefilledVerifiedMobileNumber = ref("");

// Referrer
const referrerVerified = ref(false);
const referrerLoading = ref(false);
const referrerError = ref("");
const referrerMaskedName = ref("");

// Aadhaar
const aadhaarVerified = ref(false);
const aadhaarLoading = ref(false);
const aadhaarOtpSent = ref(false);
const aadhaarOtp = ref("");
const aadhaarOtpVerifying = ref(false);
const aadhaarValidationId = ref("");
const aadhaarError = ref("");
const aadhaarStatusMessage = ref("");
const aadhaarAddress = ref("");
const aadhaarAddressLoading = ref(false);
const saveAsBilling = ref(false);
const aadhaarVerifiedInCurrentFlow = ref(false);
const digilockerInitiating = ref(false);
const digilockerPopupActive = ref(false);
const digilockerStatusChecking = ref(false);
let digilockerPopupRef = null;
let digilockerPopupPollId = null;
let digilockerSessionContext = null;

const isDigilockerFlowBlocking = computed(() => {
  return (
    digilockerInitiating.value ||
    digilockerPopupActive.value ||
    digilockerStatusChecking.value
  );
});

// PAN
const panVerified = ref(false);
const panLoading = ref(false);
const panError = ref("");
const panHolderName = ref("");

const isHufSelected = computed(() => panApplicantType.value === "huf");

const isHufDetailsValid = computed(() => {
  if (!isHufSelected.value) return true;

  const hufName = String(dist.value.hufName || "").trim();
  const hufDate = String(dist.value.hufDateOfIncorporation || "").trim();
  return !!hufName && !!hufDate;
});

const canVerifyPan = computed(() => {
  const panLength = String(dist.value.pan || "").trim().length;
  return panLength === 10 && !panLoading.value && isHufDetailsValid.value;
});

// Step errors
const step1Error = ref("");
const step2Error = ref("");
const step3Error = ref("");
const step4Error = ref("");
const step1Saving = ref(false);
const step2Saving = ref(false);
const step3Saving = ref(false);
const bankAccountVerified = ref(false);

function resetDeclarationConsentText() {
  declaration.value = "";
}

function resetEsignatureConsentFields() {
  signatureFullName.value = "";
  signaturePan.value = "";
}

function resetConsentStepFields() {
  resetDeclarationConsentText();
  resetEsignatureConsentFields();
}

function resetReferrerVerification() {
  referrerVerified.value = false;
  referrerError.value = "";
  referrerMaskedName.value = "";
}

function resetEmailVerification() {
  emailVerified.value = false;
  emailImported.value = false;
  importedReferenceCode.value = "";
  emailOtpSent.value = false;
  emailOtp.value = "";
  emailValidationId.value = "";
  emailError.value = "";
  emailApiResponse.value = "";
  resetReferrerVerification();
  resetMobileVerification();
}

function resetMobileVerification() {
  mobileVerified.value = false;
  mobileOtpSent.value = false;
  mobileOtp.value = "";
  mobileValidationId.value = "";
  mobileError.value = "";
  mobileApiResponse.value = "";
  prefilledVerifiedMobileNumber.value = "";
}

function resetPanVerification() {
  panVerified.value = false;
  panError.value = "";
  panHolderName.value = "";
}

function resetBankVerification() {
  bankAccountVerified.value = false;
  dist.value.bankAccountHolder = "";
}

function resetAadhaarVerification() {
  stopDigilockerFlow();
  aadhaarVerified.value = false;
  aadhaarOtpSent.value = false;
  aadhaarOtp.value = "";
  aadhaarOtpVerifying.value = false;
  aadhaarValidationId.value = "";
  aadhaarError.value = "";
  aadhaarStatusMessage.value = "";
  aadhaarAddress.value = "";
  aadhaarAddressLoading.value = false;
  saveAsBilling.value = false;
  aadhaarVerifiedInCurrentFlow.value = false;
  resetPanVerification();
}

function clearAadhaarStateForStepBack() {
  dist.value.aadhaar = "";
  dist.value.pan = "";
  resetAadhaarVerification();
}

function clearHufPanDetails() {
  dist.value.hufName = "";
  dist.value.hufDateOfIncorporation = "";
  dist.value.pan = "";
  resetPanVerification();
}

function selectPanApplicantType(type) {
  const nextType = String(type || "").toLowerCase();
  if (nextType !== "individual" && nextType !== "huf") return;
  if (nextType === panApplicantType.value) return;

  if (panApplicantType.value === "huf" && nextType === "individual") {
    pendingPanApplicantType.value = "individual";
    showPanTypeSwitchDialog.value = true;
    return;
  }

  panApplicantType.value = nextType;
  resetPanVerification();
}

function cancelPanApplicantTypeSwitch() {
  showPanTypeSwitchDialog.value = false;
  pendingPanApplicantType.value = panApplicantType.value;
}

function confirmPanApplicantTypeSwitch() {
  if (pendingPanApplicantType.value === "individual") {
    panApplicantType.value = "individual";
    clearHufPanDetails();
  }

  showPanTypeSwitchDialog.value = false;
  pendingPanApplicantType.value = panApplicantType.value;
}

function handleKycStepBack() {
  if (!aadhaarVerified.value) {
    distStep.value = 2;
    return;
  }

  showAadhaarBackDialog.value = true;
}

function cancelKycStepBack() {
  showAadhaarBackDialog.value = false;
}

function confirmKycStepBack() {
  showAadhaarBackDialog.value = false;
  clearAadhaarStateForStepBack();
  distStep.value = 2;
}

watch(
  () => dist.value.email,
  (newValue, oldValue) => {
    if (oldValue === undefined) return;
    if (String(newValue || "") === String(oldValue || "")) return;
    resetEmailVerification();
  },
);

watch(
  () => dist.value.mobile,
  (newValue, oldValue) => {
    if (oldValue === undefined) return;
    if (String(newValue || "") === String(oldValue || "")) return;

    const normalizedNewValue = String(newValue || "").trim();
    const normalizedPrefilledMobile = String(
      prefilledVerifiedMobileNumber.value || "",
    ).trim();

    if (
      normalizedNewValue &&
      normalizedPrefilledMobile &&
      normalizedNewValue === normalizedPrefilledMobile
    ) {
      return;
    }

    resetMobileVerification();
  },
);

watch(
  () => dist.value.referrerId,
  (newValue, oldValue) => {
    if (oldValue === undefined) return;
    if (String(newValue || "") === String(oldValue || "")) return;
    if (
      String(newValue || "").trim() &&
      String(newValue || "").trim() ===
        String(importedReferenceCode.value || "").trim()
    ) {
      return;
    }
    resetReferrerVerification();
  },
);

watch(
  () => dist.value.aadhaar,
  (newValue, oldValue) => {
    if (oldValue === undefined) return;
    if (String(newValue || "") === String(oldValue || "")) return;
    resetAadhaarVerification();
  },
);

watch(
  () => dist.value.pan,
  (newValue, oldValue) => {
    if (oldValue === undefined) return;
    if (String(newValue || "") === String(oldValue || "")) return;
    resetPanVerification();
  },
);

watch(
  () => dist.value.hufName,
  (newValue, oldValue) => {
    if (oldValue === undefined) return;
    if (String(newValue || "") === String(oldValue || "")) return;
    resetPanVerification();
  },
);

watch(
  () => dist.value.hufDateOfIncorporation,
  (newValue, oldValue) => {
    if (oldValue === undefined) return;
    if (String(newValue || "") === String(oldValue || "")) return;
    resetPanVerification();
  },
);

watch(
  () => dist.value.bankAccount,
  (newValue, oldValue) => {
    if (oldValue === undefined) return;
    if (String(newValue || "") === String(oldValue || "")) return;
    resetBankVerification();
  },
);

watch(
  () => dist.value.bankAccountConfirm,
  (newValue, oldValue) => {
    if (oldValue === undefined) return;
    if (String(newValue || "") === String(oldValue || "")) return;
    resetBankVerification();
  },
);

watch(
  () => dist.value.ifsc,
  (newValue, oldValue) => {
    if (oldValue === undefined) return;
    if (String(newValue || "") === String(oldValue || "")) return;
    resetBankVerification();
  },
);

// If the user leaves the KYC step (step 3) before completing Aadhaar
// verification — e.g. an OTP was sent/typed but never confirmed, and they
// hit Back — clear the in-flight OTP state so returning to the step always
// starts a clean "Send OTP" flow rather than resuming a stale, unverified OTP.
watch(distStep, (newStep, oldStep) => {
  if (
    (newStep === 1 || newStep === 2) &&
    !(aadhaarVerified.value && panVerified.value)
  ) {
    aadhaarVerifiedInCurrentFlow.value = false;
  }

  if (oldStep === 3 && newStep !== 3 && !aadhaarVerified.value) {
    stopDigilockerFlow();
    aadhaarOtpSent.value = false;
    aadhaarOtp.value = "";
    aadhaarValidationId.value = "";
    aadhaarError.value = "";
    aadhaarOtpVerifying.value = false;
  }

  if (oldStep === 5 && newStep !== 5) {
    resetConsentStepFields();
  }
});

onBeforeRouteLeave(() => {
  stopDigilockerFlow();
  resetConsentStepFields();
});

onBeforeUnmount(() => {
  stopDigilockerFlow();
});

function getDomainNameForApi() {
  const host = window.location.host || window.location.hostname || "";
  const hostname = (window.location.hostname || "").toLowerCase();
  const isLocal =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".local");

  if (isLocal) {
    return process.env.VUE_APP_DOMAIN_NAME || host;
  }

  return host;
}

function formatAadhaar() {
  let val = dist.value.aadhaar.replace(/\D/g, "").slice(0, 12);
  val = val.replace(/(\d{4})(?=\d)/g, "$1 ");
  dist.value.aadhaar = val;
}

function formatDateForApi(dateValue) {
  const value = String(dateValue || "").trim();
  const parts = value.split("-");
  if (parts.length !== 3) return value;

  const [year, month, day] = parts;
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
}

// Inverse of formatDateForApi — converts a DD/MM/YYYY string coming back
// from the API (e.g. resumed-registration data) into the YYYY-MM-DD format
// a native <input type="date"> expects.
function formatDateFromApi(dateValue) {
  const value = String(dateValue || "").trim();
  const parts = value.split("/");
  if (parts.length !== 3) return "";

  const [day, month, year] = parts;
  if (!day || !month || !year) return "";

  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(
    2,
    "0",
  )}`;
}

function getAadhaarAddressText(addressValue) {
  if (!addressValue) return "";

  if (typeof addressValue === "string") {
    return addressValue.trim();
  }

  if (typeof addressValue === "object") {
    return [
      addressValue.addressLine1,
      addressValue.addressLine2,
      addressValue.addressLine3,
      addressValue.city,
      addressValue.state,
      addressValue.country,
      addressValue.pinCode,
    ]
      .filter((item) => String(item || "").trim())
      .join(", ");
  }

  return "";
}

function toAadhaarAddressPayload(addressValue) {
  if (addressValue && typeof addressValue === "object") {
    return {
      addressLine1: String(addressValue.addressLine1 || "").trim(),
      addressLine2: String(addressValue.addressLine2 || "").trim(),
      addressLine3: String(addressValue.addressLine3 || "").trim(),
      city: String(addressValue.city || "").trim(),
      state: String(addressValue.state || "").trim(),
      country: String(addressValue.country || "India").trim() || "India",
      pinCode: String(addressValue.pinCode || "").trim(),
    };
  }

  const text = String(addressValue || "").trim();
  const parts = text
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  const tail = parts[parts.length - 1] || "";
  const pinCodeMatch = tail.match(/(\d{6})/);
  const pinCode = pinCodeMatch?.[1] || "";
  const state = pinCode
    ? tail.replace(/[-\s]*\d{6}\s*$/, "").trim()
    : String(tail || "").trim();
  const city = parts.length >= 2 ? String(parts[parts.length - 2] || "") : "";
  const lineParts = parts.slice(0, Math.max(parts.length - 2, 0));

  return {
    addressLine1: String(lineParts[0] || "").trim(),
    addressLine2: String(lineParts[1] || "").trim(),
    addressLine3: String(lineParts.slice(2).join(", ") || "").trim(),
    city: String(city || "").trim(),
    state,
    country: "India",
    pinCode,
  };
}

// ─── RESUMED-REGISTRATION HYDRATION ───
// Several endpoints (referrer validation, bank validation, imported-email
// verification) return the applicant's already-saved profile when someone
// resumes a partially completed registration. Whenever we see that data,
// pre-fill it and mark the relevant steps as already verified so the user
// isn't asked to redo work — without ever overwriting something they've
// already typed in this session.
function hydrateResumedRegistrationData(responseData) {
  if (!responseData || typeof responseData !== "object") return;

  const nestedData = responseData?.data || {};

  const respName = String(responseData?.name || "").trim();
  if (respName && !dist.value.fullName.trim()) {
    dist.value.fullName = respName;
  }

  const respDob = String(responseData?.dateOfBirth || "").trim();
  if (respDob && !dist.value.dob) {
    const formatted = formatDateFromApi(respDob);
    if (formatted) {
      dist.value.dob = formatted;
    }
  }

  const respMobile = String(responseData?.mobileNumber || "").trim();
  if (respMobile && !dist.value.mobile.trim()) {
    dist.value.mobile = respMobile;
  }

  const mobileVerifiedRaw = responseData?.mobileVerified;
  const isMobileVerifiedFromApi =
    mobileVerifiedRaw === true ||
    mobileVerifiedRaw === 1 ||
    String(mobileVerifiedRaw || "").toLowerCase() === "true";

  if (isMobileVerifiedFromApi) {
    const verifiedMobileNumber = String(
      respMobile || dist.value.mobile || "",
    ).trim();

    if (verifiedMobileNumber) {
      prefilledVerifiedMobileNumber.value = verifiedMobileNumber;
      dist.value.mobile = verifiedMobileNumber;
    }

    mobileVerified.value = true;
    mobileOtpSent.value = false;
    mobileOtp.value = "";
    mobileValidationId.value = "";
    mobileError.value = "";
  }

  if (responseData?.aadhaarVerified === true) {
    const rawAadhaarValue = String(
      responseData?.aadhaarNumber ||
        responseData?.aadhaarNo ||
        responseData?.aadhaar ||
        nestedData?.aadhaarNumber ||
        nestedData?.aadhaarNo ||
        nestedData?.aadhaar ||
        "",
    ).trim();
    const aadhaarDigits = rawAadhaarValue.replace(/\D/g, "");
    if (aadhaarDigits.length === 12 && !dist.value.aadhaar.trim()) {
      dist.value.aadhaar = aadhaarDigits
        .replace(/(\d{4})(?=\d)/g, "$1 ")
        .trim();
    }

    aadhaarVerified.value = true;
    aadhaarVerifiedInCurrentFlow.value = true;
    aadhaarOtpSent.value = false;
    aadhaarAddressLoading.value = false;
    const respAddress = responseData?.aadhaarAddress;
    if (respAddress && !getAadhaarAddressText(aadhaarAddress.value)) {
      aadhaarAddress.value = respAddress;
    }
    if (responseData?.saveAadhaarAddress === true) {
      saveAsBilling.value = true;
    }
  }

  if (responseData?.panVerified === true) {
    panVerified.value = true;
  }

  const hufRaw = responseData?.huf ?? nestedData?.huf;
  const isHufFromApi =
    hufRaw === true ||
    hufRaw === 1 ||
    String(hufRaw || "").toLowerCase() === "true";

  panApplicantType.value = isHufFromApi ? "huf" : "individual";

  const respPanNumber = String(
    responseData?.panNumber || responseData?.pan || nestedData?.panNumber || "",
  )
    .trim()
    .toUpperCase();

  if (respPanNumber && !dist.value.pan.trim()) {
    dist.value.pan = respPanNumber;
  }

  if (isHufFromApi) {
    const respHufName = String(
      responseData?.hufName || nestedData?.hufName || "",
    ).trim();
    const respHufDateRaw = String(
      responseData?.hufDateOfIncorporation ||
        nestedData?.hufDateOfIncorporation ||
        "",
    ).trim();
    const respHufDateFormatted = formatDateFromApi(respHufDateRaw);

    if (respHufName && !dist.value.hufName.trim()) {
      dist.value.hufName = respHufName;
    }

    if (respHufDateFormatted && !dist.value.hufDateOfIncorporation) {
      dist.value.hufDateOfIncorporation = respHufDateFormatted;
    }
  }

  const bankValidatedRaw =
    responseData?.bankAccountValidated ?? nestedData?.bankAccountValidated;
  const isBankValidated =
    bankValidatedRaw === true ||
    bankValidatedRaw === 1 ||
    String(bankValidatedRaw || "").toLowerCase() === "true";

  if (isBankValidated) {
    bankAccountVerified.value = true;
    bankSkipped.value = false;
    const respHolderName = String(
      responseData?.bankAccountHolderName ||
        nestedData?.bankAccountHolderName ||
        "",
    ).trim();
    if (respHolderName) {
      dist.value.bankAccountHolder = respHolderName;
    }
  }
}

// ─── AUTH TOKEN EXTRACTION / AUTO-LOGIN ───

function findAuthToken(obj, depth = 0, seen = new Set()) {
  if (!obj || typeof obj !== "object" || depth > 5) return "";
  if (seen.has(obj)) return "";
  seen.add(obj);

  const tokenKeys = ["token", "Authorization", "bearerToken"];

  for (const key of tokenKeys) {
    const value = obj[key];
    if (value && typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value && typeof value === "object") {
      const found = findAuthToken(value, depth + 1, seen);
      if (found) return found;
    }
  }

  return "";
}

function extractAuthTokenFromResponse(responseData) {
  return findAuthToken(responseData);
}

function extractAuthTokenFromHeaders(rawResponse) {
  const headers = rawResponse?.headers || rawResponse?.response?.headers || {};
  const headerToken =
    headers?.authorization ||
    headers?.Authorization ||
    headers?.AUTHORIZATION ||
    headers?.["x-authorization"] ||
    headers?.["X-Authorization"] ||
    "";

  if (Array.isArray(headerToken)) {
    return String(headerToken[0] || "").trim();
  }

  return String(headerToken || "").trim();
}

function hasAuthenticatedSession() {
  const sdkToken = String(getToken() || "").trim();
  if (sdkToken) {
    return true;
  }

  try {
    const savedAuth = JSON.parse(
      window.localStorage.getItem("auth.currentUser") || "null",
    );
    return Boolean(String(savedAuth?.token || "").trim());
  } catch (error) {
    return false;
  }
}

function hasHydratedRegisteredUserData() {
  try {
    const authRaw = window.localStorage.getItem("auth.currentUser") || "null";
    const userDetailsRaw =
      window.localStorage.getItem("user_details") || "null";

    const authUser = JSON.parse(authRaw);
    const userDetails = JSON.parse(userDetailsRaw);

    const hasAuthUserObject =
      authUser &&
      typeof authUser === "object" &&
      Object.keys(authUser).length > 0;
    const hasUserDetailsObject =
      userDetails &&
      typeof userDetails === "object" &&
      Object.keys(userDetails).length > 0;

    return Boolean(hasAuthUserObject && hasUserDetailsObject);
  } catch (error) {
    return false;
  }
}

function persistRegisteredLoginSession(loginResponseData, token) {
  const safeToken = String(token || "").trim();
  if (!safeToken) {
    return false;
  }

  const loginPayload = {
    ...(loginResponseData?.data || {}),
    ...(loginResponseData || {}),
  };

  const currentUser = {
    ...loginPayload,
    token: safeToken,
  };

  setToken(safeToken);
  window.localStorage.setItem("auth.currentUser", JSON.stringify(currentUser));
  window.localStorage.setItem("user_details", JSON.stringify(loginPayload));
  return true;
}

/**
 * Attempts to establish a logged-in session right after registration
 * strictly from register response data and response headers.
 */
async function performAutoLogin(responseData, rawResponse = null) {
  const loginResponseData =
    responseData?.loginResponse || responseData?.data?.loginResponse;
  const loginStatusCode = String(
    loginResponseData?.statusCode ?? loginResponseData?.data?.statusCode ?? "",
  );

  // Registration is considered auto-login capable only when loginResponse is successful.
  if (!loginResponseData || loginStatusCode !== "200") {
    return false;
  }

  const headerAuthToken = extractAuthTokenFromHeaders(rawResponse);
  const loginResponseToken = extractAuthTokenFromResponse(loginResponseData);
  const registerResponseToken = extractAuthTokenFromResponse(responseData);
  const resolvedToken =
    headerAuthToken ||
    loginResponseToken ||
    registerResponseToken ||
    String(getToken() || "").trim();

  if (!resolvedToken) {
    return false;
  }

  const didPersist = persistRegisteredLoginSession(
    loginResponseData,
    resolvedToken,
  );
  if (!didPersist) {
    return false;
  }

  return hasAuthenticatedSession();
}

async function handleShopNowClick() {
  shopNowError.value = "";
  const responseData = latestRegisterResponse.value;
  const rawResponse = latestRegisterRawResponse.value;
  const shouldHydrateFromResponse =
    !!responseData &&
    (!hasAuthenticatedSession() || !hasHydratedRegisteredUserData());

  // If token/session fields are incomplete, hydrate using register response.
  if (shouldHydrateFromResponse) {
    const isAutoLoginSuccessful = await performAutoLogin(
      responseData,
      rawResponse,
    );

    if (!isAutoLoginSuccessful) {
      shopNowError.value =
        "We couldn't fully restore your session, but you can continue to shop now.";
    }
  }

  router.push("/shop");
}

function handlePlacementClick() {
  const distributorCode = registrationResponseData.value?.distributorCode;
  const name = registrationResponseData.value?.name;

  shopNowError.value = "";
  // ${BA_URL}/?step=2_self&newBaId=BA-0004&newBaName=Tim%20Cook&registrationToken=abc
  const url = `${BA_URL}/?step=2_self&newBaId=${encodeURIComponent(
    distributorCode,
  )}&newBaName=${encodeURIComponent(name)}&registrationToken=abc`;
  console.log("placement URL:", url);
  placementUrl.value = url;
  showPlacementPopup.value = true;
}

function getReferrerMaskedNameFromResponse(responseData) {
  const nested = responseData?.data || {};
  const referrer = responseData?.referrer || nested?.referrer || {};

  return String(
    responseData?.refererName ||
      nested?.refererName ||
      responseData?.referrerName ||
      nested?.referrerName ||
      responseData?.maskedName ||
      nested?.maskedName ||
      responseData?.referrerMaskedName ||
      nested?.referrerMaskedName ||
      referrer?.maskedName ||
      referrer?.name ||
      "",
  ).trim();
}

// ─── MOCK VERIFICATIONS ───

async function sendEmailOtp() {
  emailOtpLoading.value = true;
  emailError.value = "";
  emailApiResponse.value = "";
  emailVerified.value = false;

  const emailValue = String(dist.value.email || "").trim();
  const domainName = getDomainNameForApi();

  if (!emailValue || !domainName) {
    emailError.value = "Email and domain are required.";
    emailOtpLoading.value = false;
    return;
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(emailValue)) {
    emailError.value = "Please enter a valid email address.";
    emailOtpLoading.value = false;
    return;
  }

  try {
    const response = await sendRegisterVerifyEmailOtp({
      email: emailValue,
      domainName,
    });

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "");
    const validationId =
      responseData?.emailValidationId ||
      responseData?.data?.emailValidationId ||
      "";
    const statusMessage =
      responseData?.statusMessage ||
      responseData?.message ||
      "OTP sent successfully.";

    emailApiResponse.value = statusMessage;
    emailValidationId.value = String(validationId || "");

    if (statusCode === "200") {
      emailOtpSent.value = true;
      emailOtp.value = "";
      emailError.value = "";
    } else {
      emailError.value = statusMessage;
      emailOtpSent.value = false;
      emailValidationId.value = "";
    }
  } catch (error) {
    emailOtpSent.value = false;
    emailValidationId.value = "";
    emailError.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "Unable to send email OTP. Please try again.";
  } finally {
    emailOtpLoading.value = false;
  }
}

async function verifyEmailOtp() {
  emailError.value = "";
  emailApiResponse.value = "";

  const emailValue = String(dist.value.email || "").trim();
  const domainName = getDomainNameForApi();
  const otp = String(emailOtp.value || "").trim();

  if (!emailValidationId.value) {
    emailError.value = "Please send OTP first.";
    return;
  }

  if (otp.length < 4) {
    emailError.value = "Please enter a valid OTP.";
    return;
  }

  try {
    const response = await validateRegisterVerifyEmailOtp({
      email: emailValue,
      domainName,
      emailValidationId: emailValidationId.value,
      emailOtp: otp,
    });

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "");
    const importedRaw =
      responseData?.imported ?? responseData?.data?.imported ?? false;
    const responseReferenceCode = String(
      responseData?.referenceCode ?? responseData?.data?.referenceCode ?? "",
    ).trim();
    const imported =
      importedRaw === true ||
      importedRaw === 1 ||
      String(importedRaw || "").toLowerCase() === "true";
    const statusMessage =
      responseData?.statusMessage ||
      responseData?.message ||
      "Email verified successfully.";

    if (statusCode === "200") {
      emailVerified.value = true;
      emailImported.value = imported;
      importedReferenceCode.value = responseReferenceCode;
      if (responseReferenceCode) {
        dist.value.referrerId = responseReferenceCode;
        referrerVerified.value = true;
        referrerError.value = "";
        referrerMaskedName.value =
          getReferrerMaskedNameFromResponse(responseData) ||
          referrerMaskedName.value;
      } else if (!imported) {
        referrerVerified.value = false;
        dist.value.referrerId = "";
        referrerMaskedName.value = "";
      }
      // Resumed applicants may already have identity/KYC/bank data saved —
      // hydrate whatever this response carries.
      hydrateResumedRegistrationData(responseData);
      emailOtpSent.value = false;
      emailApiResponse.value = statusMessage;
      emailError.value = "";
    } else {
      emailError.value = statusMessage;
    }
  } catch (error) {
    emailError.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "Email OTP verification failed. Please try again.";
  }
}

async function sendMobileOtp() {
  mobileOtpLoading.value = true;
  mobileError.value = "";
  mobileApiResponse.value = "";
  mobileVerified.value = false;

  const mobileNumber = String(dist.value.mobile || "").trim();
  const emailValue = String(dist.value.email || "").trim();
  const domainName = getDomainNameForApi();

  if (!emailVerified.value) {
    mobileError.value = "Please verify your email first.";
    mobileOtpLoading.value = false;
    return;
  }

  if (!mobileNumber || !domainName) {
    mobileError.value = "Mobile number and domain are required.";
    mobileOtpLoading.value = false;
    return;
  }

  try {
    const response = await sendRegisterVerifyMobileOtp({
      email: emailValue,
      mobileNumber,
      domainName,
    });

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "200");
    const validationId =
      responseData?.mobileValidationId ||
      responseData?.data?.mobileValidationId ||
      "";
    const statusMessage =
      responseData?.statusMessage ||
      responseData?.message ||
      "OTP sent successfully.";

    mobileApiResponse.value = statusMessage;
    mobileValidationId.value = String(validationId || "");

    if (statusCode === "200") {
      mobileOtpSent.value = true;
      mobileOtp.value = "";
      mobileError.value = "";
    } else {
      mobileError.value = statusMessage;
      mobileOtpSent.value = false;
      mobileValidationId.value = "";
    }
  } catch (error) {
    mobileOtpSent.value = false;
    mobileValidationId.value = "";
    mobileError.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "Unable to validate mobile number. Please try again.";
  } finally {
    mobileOtpLoading.value = false;
  }
}

async function verifyMobileOtp() {
  mobileError.value = "";
  mobileApiResponse.value = "";

  const mobileNumber = String(dist.value.mobile || "").trim();
  const emailValue = String(dist.value.email || "").trim();
  const domainName = getDomainNameForApi();
  const otp = String(mobileOtp.value || "").trim();

  if (!emailVerified.value) {
    mobileError.value = "Please verify your email first.";
    return;
  }

  if (!mobileValidationId.value) {
    mobileError.value = "Please send OTP first.";
    return;
  }

  if (otp.length < 4) {
    mobileError.value = "Please enter a valid 6-digit OTP.";
    return;
  }

  try {
    const response = await validateRegisterVerifyMobileOtp({
      email: emailValue,
      mobileNumber,
      domainName,
      mobileValidationId: mobileValidationId.value,
      mobileOtp: otp,
    });

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "");
    const statusMessage =
      responseData?.statusMessage ||
      responseData?.message ||
      "Mobile verified successfully.";

    if (statusCode === "200") {
      mobileVerified.value = true;
      mobileOtpSent.value = false;
      mobileApiResponse.value = statusMessage;
      mobileError.value = "";
    } else {
      mobileError.value = statusMessage;
    }
  } catch (error) {
    mobileError.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "OTP verification failed. Please try again.";
  }
}

async function verifyReferrer() {
  referrerLoading.value = true;
  referrerError.value = "";
  referrerMaskedName.value = "";
  const emailValue = String(dist.value.email || "").trim();
  const referenceCode = String(dist.value.referrerId || "").trim();
  const domainName = getDomainNameForApi();

  if (!emailValue || !domainName || !referenceCode) {
    referrerError.value = "Email, domain, and reference code are required.";
    referrerLoading.value = false;
    return;
  }

  try {
    const response = await validateRegisterReference({
      email: emailValue,
      domainName,
      referenceCode,
    });

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "");
    const statusMessage =
      responseData?.statusMessage ||
      responseData?.message ||
      "Reference code verified successfully.";

    if (statusCode === "200") {
      referrerVerified.value = true;
      referrerError.value = "";
      // Show the masked referrer name (e.g. "J**n R***d De") from the API,
      // never the raw reference code.
      referrerMaskedName.value =
        getReferrerMaskedNameFromResponse(responseData) || "Verified Referrer";
      // This response can also carry the applicant's own already-saved
      // profile if they're resuming a partial registration.
      hydrateResumedRegistrationData(responseData);
    } else {
      referrerVerified.value = false;
      referrerError.value = statusMessage;
    }
  } catch (error) {
    referrerVerified.value = false;
    referrerError.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "Referrer ID not found. Please check and try again.";
  } finally {
    referrerLoading.value = false;
  }
}

async function sendAadhaarOtp() {
  if (aadhaarOtpVerifying.value) {
    return;
  }

  aadhaarLoading.value = true;
  aadhaarError.value = "";
  aadhaarStatusMessage.value = "";
  aadhaarValidationId.value = "";

  const mobileNumber = String(dist.value.mobile || "").trim();
  const domainName = getDomainNameForApi();
  const aadhaarNumber = String(dist.value.aadhaar || "").replace(/\s/g, "");

  if (!mobileNumber || !domainName || aadhaarNumber.length < 12) {
    aadhaarError.value =
      "Mobile number, domain, and valid Aadhaar number are required.";
    aadhaarLoading.value = false;
    return;
  }

  try {
    const response = await sendRegisterVerifyAadhaarOtp({
      mobileNumber,
      domainName,
      aadhaarNumber,
    });

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "");
    const validationId =
      responseData?.aadhaarValidationId ||
      responseData?.data?.aadhaarValidationId ||
      "";
    const statusMessage =
      responseData?.statusMessage ||
      responseData?.message ||
      "Aadhaar OTP sent successfully.";

    if (statusCode === "200") {
      aadhaarOtpSent.value = true;
      aadhaarValidationId.value = String(validationId || "");
      aadhaarError.value = "";
    } else {
      aadhaarOtpSent.value = false;
      aadhaarError.value = statusMessage;
      aadhaarValidationId.value = "";
    }
  } catch (error) {
    aadhaarOtpSent.value = false;
    aadhaarValidationId.value = "";
    aadhaarError.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "Unable to send Aadhaar OTP. Please try again.";
  } finally {
    aadhaarLoading.value = false;
  }
}

function stopDigilockerFlow() {
  if (digilockerPopupPollId) {
    window.clearInterval(digilockerPopupPollId);
    digilockerPopupPollId = null;
  }

  if (digilockerPopupRef && !digilockerPopupRef.closed) {
    digilockerPopupRef.close();
  }

  digilockerPopupRef = null;
  digilockerPopupActive.value = false;
  digilockerSessionContext = null;
  digilockerInitiating.value = false;
  digilockerStatusChecking.value = false;
}

async function checkDigilockerSessionStatusAfterPopupClose() {
  const mobileNumber = String(
    digilockerSessionContext?.mobileNumber || "",
  ).trim();
  const domainName = String(digilockerSessionContext?.domainName || "").trim();
  const aadhaarNumber = String(digilockerSessionContext?.aadhaarNumber || "")
    .replace(/\s/g, "")
    .trim();

  if (!mobileNumber || !domainName || aadhaarNumber.length < 12) {
    aadhaarError.value =
      "Unable to check DigiLocker status. Please try verification again.";
    return;
  }

  digilockerStatusChecking.value = true;
  aadhaarError.value = "";

  try {
    const response = await Promise.race([
      checkRegisterVerifyAadhaarDigilockerSession({
        mobileNumber,
        domainName,
        aadhaarNumber,
      }),
      new Promise((_, reject) => {
        window.setTimeout(() => {
          reject(new Error("DIGILOCKER_STATUS_TIMEOUT"));
        }, DIGILOCKER_STATUS_CHECK_TIMEOUT_MS);
      }),
    ]);

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "").trim();
    const sessionStatus = String(
      responseData?.sessionStatus || responseData?.data?.sessionStatus || "",
    )
      .trim()
      .toUpperCase();
    const statusMessage = String(
      responseData?.statusMessage ||
        responseData?.message ||
        "Unable to verify DigiLocker session.",
    ).trim();

    if (statusCode === "200" && sessionStatus === "SUCCEEDED") {
      const aadhaarAddressFromStatus =
        responseData?.aadhaarAddress ||
        responseData?.address ||
        responseData?.data?.aadhaarAddress ||
        responseData?.data?.address ||
        "";

      aadhaarVerified.value = true;
      aadhaarVerifiedInCurrentFlow.value = true;
      aadhaarOtpSent.value = false;
      aadhaarOtp.value = "";
      aadhaarValidationId.value = "";
      aadhaarError.value = "";
      aadhaarStatusMessage.value =
        statusMessage || "Aadhaar verified successfully.";

      if (getAadhaarAddressText(aadhaarAddressFromStatus)) {
        aadhaarAddress.value = "";
        aadhaarAddressLoading.value = true;
        setTimeout(() => {
          aadhaarAddress.value = aadhaarAddressFromStatus;
          aadhaarAddressLoading.value = false;
        }, 900);
      }

      return;
    }

    aadhaarVerified.value = false;
    aadhaarVerifiedInCurrentFlow.value = false;
    aadhaarStatusMessage.value = "";
    aadhaarError.value =
      statusMessage || "DigiLocker session verification failed.";
  } catch (error) {
    aadhaarVerified.value = false;
    aadhaarVerifiedInCurrentFlow.value = false;
    aadhaarStatusMessage.value = "";
    const errorMessage = String(error?.message || "").trim();
    if (errorMessage === "DIGILOCKER_STATUS_TIMEOUT") {
      aadhaarError.value =
        "Status check is taking too long. Please click DigiLocker again to retry.";
      return;
    }

    aadhaarError.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "Unable to check DigiLocker session status.";
  } finally {
    digilockerStatusChecking.value = false;
    digilockerSessionContext = null;
  }
}

function watchDigilockerPopupClose() {
  if (digilockerPopupPollId) {
    window.clearInterval(digilockerPopupPollId);
  }

  digilockerPopupPollId = window.setInterval(async () => {
    if (!digilockerPopupRef) {
      return;
    }

    if (digilockerPopupRef.closed) {
      window.clearInterval(digilockerPopupPollId);
      digilockerPopupPollId = null;
      digilockerPopupRef = null;
      digilockerPopupActive.value = false;
      await checkDigilockerSessionStatusAfterPopupClose();
    }
  }, 1200);
}

async function initiateDigilockerVerification() {
  if (digilockerInitiating.value) {
    return;
  }

  digilockerInitiating.value = true;
  aadhaarError.value = "";
  aadhaarStatusMessage.value = "";

  const mobileNumber = String(dist.value.mobile || "").trim();
  const domainName = getDomainNameForApi();
  const aadhaarNumber = String(dist.value.aadhaar || "").replace(/\s/g, "");
  const randomId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const payloadStorageKey = `${randomId}`;
  const digilockerRoute = router.resolve({
    name: "digilocker",
    query: {
      payloadKey: payloadStorageKey,
    },
  });

  // const digilockerPageUrl = `${window.location.origin}${digilockerRoute.href}`;
  const digilockerPageUrl = `${window.location.origin}${digilockerRoute.href}`;

  if (!mobileNumber || !domainName || aadhaarNumber.length < 12) {
    aadhaarError.value =
      "Mobile number, domain, and valid Aadhaar number are required.";
    digilockerInitiating.value = false;
    return;
  }

  try {
    const response = await initiateRegisterVerifyAadhaarDigilockerSession({
      mobileNumber,
      domainName,
      aadhaarNumber,
      digilockerRedirectUrl: digilockerPageUrl,
    });

    const responseData = response?.data ?? response ?? {};

    const statusCode = String(responseData?.statusCode ?? "");
    const statusMessage =
      responseData?.statusMessage ||
      responseData?.message ||
      "Unable to initiate DigiLocker session.";
    const authorizationUrl = String(
      responseData?.authorizationUrl ||
        responseData?.data?.authorizationUrl ||
        "",
    ).trim();

    if (statusCode !== "200" || !authorizationUrl) {
      aadhaarError.value = statusMessage;
      return;
    }

    window.localStorage.setItem(
      payloadStorageKey,
      JSON.stringify({
        responseData,
        authorizationUrl,
        mobileNumber,
        domainName,
        aadhaarNumber,
        createdAt: Date.now(),
      }),
    );

    digilockerSessionContext = {
      mobileNumber,
      domainName,
      aadhaarNumber,
    };

    const digilockerPopup = window.open(
      digilockerPageUrl,
      "digilocker-complete",
      "width=540,height=760,left=160,top=80,resizable=yes,scrollbars=yes",
    );

    if (!digilockerPopup) {
      digilockerPopupActive.value = false;
      window.localStorage.removeItem(payloadStorageKey);
      aadhaarError.value =
        "Popup blocked by browser. Please allow popups and try again.";
      return;
    }

    digilockerPopupRef = digilockerPopup;
    digilockerPopupActive.value = true;
    digilockerPopup.focus();
    watchDigilockerPopupClose();
  } catch (error) {
    aadhaarError.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "Unable to initiate DigiLocker verification. Please try again.";
  } finally {
    digilockerInitiating.value = false;
  }
}

async function verifyAadhaarOtp() {
  if (aadhaarOtpVerifying.value) {
    return;
  }

  aadhaarOtpVerifying.value = true;
  aadhaarError.value = "";
  aadhaarStatusMessage.value = "";
  const mobileNumber = String(dist.value.mobile || "").trim();
  const domainName = getDomainNameForApi();
  const aadhaarNumber = String(dist.value.aadhaar || "").replace(/\s/g, "");
  const otp = String(aadhaarOtp.value || "").trim();

  if (!aadhaarValidationId.value) {
    aadhaarError.value = "Please send Aadhaar OTP first.";
    aadhaarOtpVerifying.value = false;
    return;
  }

  if (otp.length < 6) {
    aadhaarError.value = "Please enter a valid 6-digit OTP.";
    aadhaarOtpVerifying.value = false;
    return;
  }

  try {
    const response = await validateRegisterVerifyAadhaarOtp({
      mobileNumber,
      domainName,
      aadhaarNumber,
      aadhaarValidationId: aadhaarValidationId.value,
      aadhaarOtp: otp,
    });

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "");
    const statusMessage =
      responseData?.statusMessage ||
      responseData?.message ||
      "Aadhaar verified successfully.";

    if (statusCode === "200") {
      aadhaarVerified.value = true;
      aadhaarVerifiedInCurrentFlow.value = true;
      aadhaarOtpSent.value = false;
      aadhaarOtp.value = "";
      aadhaarError.value = "";

      const fetchedAddress =
        responseData?.aadhaarAddress ||
        responseData?.address ||
        responseData?.data?.aadhaarAddress ||
        "";

      // Address enrichment can take a moment on the backend even though
      // OTP verification itself is instant — show a short loading state
      // before revealing the address (and unlocking PAN verification).
      aadhaarAddress.value = "";
      aadhaarAddressLoading.value = true;
      setTimeout(() => {
        aadhaarAddress.value = fetchedAddress;
        aadhaarAddressLoading.value = false;
      }, 900);
    } else {
      aadhaarError.value = statusMessage;
    }
  } catch (error) {
    aadhaarError.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "Invalid Aadhaar OTP. Please try again.";
  } finally {
    aadhaarOtpVerifying.value = false;
  }
}

async function verifyPan() {
  panLoading.value = true;
  panError.value = "";

  const mobileNumber = String(dist.value.mobile || "").trim();
  const domainName = getDomainNameForApi();
  const aadhaarNumber = String(dist.value.aadhaar || "").replace(/\s/g, "");
  const panNumber = String(dist.value.pan || "")
    .trim()
    .toUpperCase();
  const isHuf = panApplicantType.value === "huf";
  const hufName = String(dist.value.hufName || "").trim();
  const hufDateRaw = String(dist.value.hufDateOfIncorporation || "").trim();

  if (
    !mobileNumber ||
    !domainName ||
    aadhaarNumber.length < 12 ||
    panNumber.length < 10
  ) {
    panError.value =
      "Mobile number, domain, valid Aadhaar, and valid PAN are required.";
    panLoading.value = false;
    return;
  }

  if (isHuf && (!hufName || !hufDateRaw)) {
    panError.value =
      "For HUF, HUF name and date of incorporation are required.";
    panLoading.value = false;
    return;
  }

  try {
    const response = await validateRegisterPan({
      mobileNumber,
      domainName,
      aadhaarNumber,
      panNumber,
      huf: isHuf,
      ...(isHuf
        ? {
            hufName,
            hufDateOfIncorporation: formatDateForApi(hufDateRaw),
          }
        : {}),
    });

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "");
    const panVerifiedRaw =
      responseData?.panVerified ?? responseData?.data?.panVerified;
    const isPanVerifiedFromResponse =
      panVerifiedRaw === true ||
      panVerifiedRaw === 1 ||
      String(panVerifiedRaw || "").toLowerCase() === "true";
    const shouldUsePanVerifiedResponse = isHuf || panVerifiedRaw !== undefined;
    const isPanVerifiedSuccess = shouldUsePanVerifiedResponse
      ? isPanVerifiedFromResponse
      : statusCode === "200";

    const panRetryMessage = isHuf
      ? "PAN/HUF verification failed. Please check PAN, HUF name, and date of incorporation and try again."
      : "PAN verification failed. Please check PAN number and try again.";

    if (statusCode === "200" && isPanVerifiedSuccess) {
      panVerified.value = true;
      panError.value = "";
      panHolderName.value =
        responseData?.panHolderName ||
        responseData?.name ||
        dist.value.fullName ||
        "Verified Account Holder";
    } else {
      panVerified.value = false;
      panError.value = panRetryMessage;
    }
  } catch (error) {
    panVerified.value = false;
    panError.value = isHuf
      ? "PAN/HUF verification failed. Please check PAN, HUF name, and date of incorporation and try again."
      : "PAN verification failed. Please check PAN number and try again.";
  } finally {
    panLoading.value = false;
  }
}

// ─── STEP NAVIGATION ───

function validateStep1() {
  step1Error.value = "";
  if (!emailVerified.value) {
    step1Error.value = "Please verify your email first.";
    return false;
  }
  return true;
}

function validateStep2() {
  step1Error.value = "";
  if (!emailVerified.value) {
    step1Error.value = "Please verify your email first.";
    return false;
  }
  if (!mobileVerified.value) {
    step1Error.value = "Please verify your mobile number first.";
    return false;
  }
  if (!emailImported.value && !referrerVerified.value) {
    step1Error.value = "Please verify your Referrer ID.";
    return false;
  }
  if (!dist.value.fullName.trim()) {
    step1Error.value = "Full name is required.";
    return false;
  }
  if (!dist.value.dob) {
    step1Error.value = "Date of birth is required.";
    return false;
  }
  if (!dist.value.password || dist.value.password.length < 8) {
    step1Error.value = "Password must be at least 8 characters.";
    return false;
  }
  if (dist.value.password !== dist.value.confirmPassword) {
    step1Error.value = "Passwords do not match.";
    return false;
  }
  return true;
}

async function goToStep2() {
  if (!validateStep1()) return;

  stepCompleted.value[0] = true;
  distStep.value = 2;
  window.scrollTo({ top: 0, behavior: "smooth" });
}

async function goToStep3() {
  if (!validateStep2()) return;

  step1Saving.value = true;
  step1Error.value = "";

  try {
    const response = await saveRegisterDetails({
      mobileNumber: String(dist.value.mobile || "").trim(),
      domainName: getDomainNameForApi(),
      name: String(dist.value.fullName || "").trim(),
      dateOfBirth: formatDateForApi(dist.value.dob),
      email: String(dist.value.email || "").trim(),
      password: dist.value.password,
    });

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "");
    const statusMessage =
      responseData?.statusMessage ||
      responseData?.message ||
      "Unable to save details. Please try again.";

    if (statusCode === "200") {
      stepCompleted.value[1] = true;
      distStep.value = 3;
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      step1Error.value = statusMessage;
    }
  } catch (error) {
    step1Error.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "Unable to save details. Please try again.";
  } finally {
    step1Saving.value = false;
  }
}

async function goToStep4() {
  step2Error.value = "";
  if (!aadhaarVerified.value) {
    step2Error.value = "Please complete Aadhaar verification.";
    return;
  }
  if (!panVerified.value) {
    step2Error.value = "Please verify your PAN before continuing.";
    return;
  }

  if (saveAsBilling.value) {
    if (!getAadhaarAddressText(aadhaarAddress.value)) {
      step2Error.value = "Aadhaar address is required to save as billing.";
      return;
    }

    step2Saving.value = true;

    try {
      const response = await saveRegisterAadhaarAddress({
        mobileNumber: String(dist.value.mobile || "").trim(),
        domainName: getDomainNameForApi(),
        saveAadhaarAddress: true,
        aadhaarAddress: toAadhaarAddressPayload(aadhaarAddress.value),
      });

      const responseData = response?.data ?? response ?? {};
      const statusCode = String(responseData?.statusCode ?? "");
      const statusMessage =
        responseData?.statusMessage ||
        responseData?.message ||
        "Unable to save Aadhaar address. Please try again.";

      if (statusCode !== "200") {
        step2Error.value = statusMessage;
        return;
      }
    } catch (error) {
      step2Error.value =
        error?.response?.data?.statusMessage ||
        error?.response?.data?.message ||
        "Unable to save Aadhaar address. Please try again.";
      return;
    } finally {
      step2Saving.value = false;
    }
  }

  stepCompleted.value[2] = true;
  distStep.value = 4;
  window.scrollTo({ top: 0, behavior: "smooth" });
}

async function verifyBankAccount() {
  step3Error.value = "";
  if (!dist.value.bankAccount.trim()) {
    step3Error.value = "Account number is required.";
    bankAccountVerified.value = false;
    return;
  }
  if (dist.value.bankAccount !== dist.value.bankAccountConfirm) {
    step3Error.value = "Account numbers do not match.";
    bankAccountVerified.value = false;
    return;
  }
  if (!dist.value.ifsc.trim()) {
    step3Error.value = "IFSC code is required.";
    bankAccountVerified.value = false;
    return;
  }

  step3Saving.value = true;
  bankAccountVerified.value = false;

  try {
    const response = await validateRegisterBankAccount({
      mobileNumber: String(dist.value.mobile || "").trim(),
      domainName: getDomainNameForApi(),
      bankAccountNumber: String(dist.value.bankAccount || "").trim(),
      bankIfsc: String(dist.value.ifsc || "")
        .trim()
        .toUpperCase(),
    });

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "");
    const statusMessage =
      responseData?.statusMessage ||
      responseData?.message ||
      "Unable to validate bank account. Please try again.";

    if (statusCode !== "200") {
      step3Error.value = statusMessage;
      return;
    }

    // Account holder name is fetched from the bank, never typed by the user.
    const respHolderName = String(
      responseData?.bankAccountHolderName || "",
    ).trim();
    if (respHolderName) {
      dist.value.bankAccountHolder = respHolderName;
    }
    bankAccountVerified.value = true;
  } catch (error) {
    step3Error.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "Unable to validate bank account. Please try again.";
    bankAccountVerified.value = false;
    return;
  } finally {
    step3Saving.value = false;
  }
}

async function goToStep5() {
  step3Error.value = "";
  if (!bankAccountVerified.value) {
    step3Error.value = "Please verify your bank account before continuing.";
    return;
  }

  const hasConsentLoaded = await fetchRegisterConsentRequirements();
  if (!hasConsentLoaded) {
    return;
  }

  bankSkipped.value = false;
  stepCompleted.value[3] = true;
  stepSkipped.value[3] = false;
  distStep.value = 5;
  window.scrollTo({ top: 0, behavior: "smooth" });
  nextTick(() => checkTncScrollComplete());
}

async function confirmSkipBank() {
  step3Error.value = "";
  const hasConsentLoaded = await fetchRegisterConsentRequirements();
  if (!hasConsentLoaded) {
    return;
  }

  showBankSkipDialog.value = false;
  bankSkipped.value = true;
  stepCompleted.value[3] = true;
  stepSkipped.value[3] = true;
  distStep.value = 5;
  window.scrollTo({ top: 0, behavior: "smooth" });
  nextTick(() => checkTncScrollComplete());
}

function toPlainConsentText(content) {
  const html = String(content || "").trim();
  if (!html) {
    return "";
  }

  if (typeof window === "undefined") {
    return html
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  return String(doc?.body?.textContent || "")
    .replace(/\s+/g, " ")
    .trim();
}

async function fetchRegisterConsentRequirements() {
  const mobileNo = String(dist.value.mobile || "").trim();
  const domainName = getDomainNameForApi();

  if (!mobileNo || !domainName) {
    step3Error.value = "Mobile number and domain are required for consent.";
    return false;
  }

  try {
    const response = await getActiveRegisterConsentRequirements({
      mobileNo,
      mobileNumber: mobileNo,
      domainName,
    });

    const responseData = response?.data ?? response ?? {};
    const statusCode = String(responseData?.statusCode ?? "");
    const statusMessage =
      responseData?.statusMessage ||
      responseData?.message ||
      "Unable to fetch consent details. Please try again.";

    if (statusCode !== "200") {
      step3Error.value = statusMessage;
      return false;
    }

    consentDocuments.value = [];
    consentHandshakeToken.value = "";

    const documents = Array.isArray(responseData?.consent?.documents)
      ? responseData.consent.documents
      : [];

    if (documents.length > 0) {
      const sortedDocuments = [...documents].sort((left, right) => {
        const leftOrder = Number(left?.displayOrder);
        const rightOrder = Number(right?.displayOrder);
        const safeLeft = Number.isFinite(leftOrder)
          ? leftOrder
          : Number.MAX_SAFE_INTEGER;
        const safeRight = Number.isFinite(rightOrder)
          ? rightOrder
          : Number.MAX_SAFE_INTEGER;
        return safeLeft - safeRight;
      });

      consentDocuments.value = sortedDocuments.map((document, index) =>
        normalizeConsentDocument(document, index),
      );
      consentHandshakeToken.value = String(
        responseData?.consent?.handshakeToken || "",
      ).trim();

      phase1Items.value = consentDocuments.value.filter((item) =>
        isPhase1ConsentDocument(item),
      );
      phase1Checks.value = phase1Items.value.map(() => false);

      phase2ConsentDocument.value =
        consentDocuments.value.find((item) => isPhase2ConsentDocument(item)) ||
        null;
      phase3ConsentDocument.value =
        consentDocuments.value.find((item) => isPhase3ConsentDocument(item)) ||
        null;
      phase4ConsentDocument.value =
        consentDocuments.value.find((item) => isPhase4ConsentDocument(item)) ||
        null;

      phase2AgreementText.value = toPlainConsentText(
        phase2ConsentDocument.value?.documentHtmlContent,
      );
      expectedDeclaration.value = resolveExpectedDeclarationFromDocument(
        phase3ConsentDocument.value,
      );
    } else {
      phase1Items.value = [...defaultPhase1Items];
      phase1Checks.value = phase1Items.value.map(() => false);
      phase2ConsentDocument.value = null;
      phase3ConsentDocument.value = null;
      phase4ConsentDocument.value = null;
      phase2AgreementText.value = "";
    }

    return true;
  } catch (error) {
    step3Error.value =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      "Unable to fetch consent details. Please try again.";
    return false;
  }
}

async function handleDistributorSubmit() {
  step4Error.value = "";
  if (!allPhase1Checked.value) {
    step4Error.value =
      "Please confirm all the compliance statements before continuing.";
    return;
  }
  if (!phase2Agreed.value) {
    step4Error.value =
      "Please read and agree to the full Distributor Agreement first.";
    return;
  }
  if (!declaration.value.trim()) {
    step4Error.value = "Please type the confirmation phrase.";
    return;
  }
  if (declarationMismatch.value) {
    step4Error.value =
      "Your confirmation phrase doesn't match. Please type the exact text shown.";
    return;
  }
  if (!signatureNameValid.value) {
    step4Error.value = "Please enter your full legal name.";
    return;
  }
  if (!signaturePanValid.value) {
    step4Error.value = "Please enter a valid PAN number (e.g. ABCDE1234F).";
    return;
  }

  const consentPayload = buildConsentPayload();
  if (!consentPayload) {
    step4Error.value =
      "Consent payload is incomplete. Please review all consent steps again.";
    return;
  }

  isSubmitting.value = true;

  try {
    const response = await authStore.loginAfterRegister({
      mobileNumber: String(dist.value.mobile || "").trim(),
      consent: consentPayload,
    });

    const responseData = response?.data ?? response ?? {};
    registrationResponseData.value = responseData;
    latestRegisterResponse.value = responseData;
    latestRegisterRawResponse.value = response?.data ? response : null;
    const statusCode = String(
      responseData?.statusCode ?? response?.statusCode ?? "",
    );
    const statusMessage =
      responseData?.statusMessage ||
      response?.statusMessage ||
      "Unable to complete registration. Please try again.";

    if (statusCode !== "200") {
      step4Error.value = statusMessage;
      showToast(statusMessage, statusCode === "400" ? "warning" : "error");
      return;
    }

    // Auto-login right after successful registration from register response.
    const isAutoLoginSuccessful = await performAutoLogin(
      responseData,
      response,
    );

    if (!isAutoLoginSuccessful) {
      step4Error.value =
        "Registration submitted, but auto-login could not be completed.";
    }

    stepCompleted.value[4] = true;
    distStep.value = 6;
    window.scrollTo({ top: 0, behavior: "smooth" });
  } catch (error) {
    const errorStatusMessage =
      error?.response?.data?.statusMessage ||
      error?.response?.data?.message ||
      error?.message ||
      "Unable to complete registration. Please try again.";

    step4Error.value = errorStatusMessage;
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
.register-section {
  --cobalt: #003da5;
  --gold: #ffc72c;
  --charcoal: #333f48;
  --dark-blue: #0a2240;
  --pure-white: #ffffff;
  --light-gray-bg: #f9fafc;
  --soft-border: rgba(0, 61, 165, 0.08);
  --green: #16a34a;
  --red: #dc2626;
  --amber: #d97706;

  padding: 50px 0 60px;
  background: linear-gradient(
    180deg,
    var(--pure-white) 0%,
    var(--light-gray-bg) 100%
  );
  color: var(--charcoal);
  width: 100%;
  box-sizing: border-box;
}

.container {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
}

.title {
  font-size: 34px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: var(--cobalt);
  margin-bottom: 24px;
  text-transform: uppercase;
}

.card-box {
  background: var(--pure-white);
  border: 1px solid var(--soft-border);
  border-radius: 20px;
  padding: 48px;
  box-sizing: border-box;
  box-shadow: 0 10px 30px rgba(10, 34, 64, 0.02);
  position: relative;
}

.card-box::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  border-radius: 20px 20px 0 0;
  background-color: var(--gold);
}

/* CUSTOMER FORM */
.role-form-title {
  margin: 0 0 22px;
  color: var(--dark-blue);
  font-size: 20px;
  font-weight: 700;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px 32px;
}

.form-group {
  margin-bottom: 0;
}
.form-group.full-span {
  grid-column: 1 / -1;
}

.form-group label {
  /* display: block; */
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
  color: var(--dark-blue);
  letter-spacing: 0.2px;
}

.optional-label {
  font-size: 12px;
  font-weight: 500;
  color: #8a94a6;
  margin-left: 4px;
}

.form-group input,
.form-group textarea {
  width: 100%;
  height: 54px;
  padding: 0 20px;
  font-size: 15px;
  color: var(--charcoal);
  border: 1px solid rgba(10, 34, 64, 0.12);
  border-radius: 12px;
  background: var(--light-gray-bg);
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;
  font-family: inherit;
}

.form-group input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.form-group textarea {
  height: auto;
  padding: 14px 20px;
  resize: none;
}

.form-group input:focus,
.form-group textarea:focus {
  background: var(--pure-white);
  border-color: var(--cobalt);
  box-shadow: 0 0 0 4px rgba(0, 61, 165, 0.08);
}

.input-verified {
  border-color: var(--green) !important;
  background: #f0fdf4 !important;
}

.input-error {
  border-color: var(--red) !important;
  background: #fff5f5 !important;
}

.bank-pending-item {
  color: var(--amber) !important;
}

.password-wrapper {
  position: relative;
}
.password-wrapper input {
  padding-right: 52px;
}

.password-visibility-btn {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.password-visibility-btn i {
  color: #8a94a6;
  font-size: 18px;
  transition: color 0.2s;
}
.password-visibility-btn:hover i {
  color: var(--cobalt);
}

/* CUSTOMER OTP BLOCK */
.otp-confirm-wrap {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px dashed rgba(0, 61, 165, 0.15);
}

.otp-sent-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f0f6ff;
  border: 1px solid rgba(0, 61, 165, 0.15);
  color: var(--cobalt);
  font-size: 13px;
  font-weight: 600;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 18px;
}

.otp-sent-banner i {
  font-size: 16px;
  flex-shrink: 0;
}

.otp-action-row {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

/* WIZARD PROGRESS */
.wizard-progress {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  margin-bottom: 36px;
  padding: 20px 0 8px;
}

.wizard-progress-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;
}

.wizard-step-dot {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 2px solid #d1dbe8;
  background: var(--pure-white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #94a3b8;
  transition: all 0.3s;
  z-index: 1;
}

.wizard-step-dot.active {
  border-color: var(--cobalt);
  color: var(--cobalt);
  box-shadow: 0 0 0 4px rgba(0, 61, 165, 0.1);
}

.wizard-step-dot.completed {
  border-color: var(--green);
  background: var(--green);
  color: var(--pure-white);
}

.wizard-step-dot.skipped {
  border-color: var(--amber);
  background: #fffbeb;
  color: var(--amber);
}

.wizard-step-label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  margin-top: 8px;
  text-align: center;
  transition: color 0.3s;
}

.wizard-step-label.active {
  color: var(--cobalt);
}
.wizard-step-label.completed {
  color: var(--green);
}
.wizard-step-label.skipped {
  color: var(--amber);
}

.wizard-step-line {
  position: absolute;
  top: 19px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: #d1dbe8;
  z-index: 0;
  transition: background 0.3s;
}

.wizard-step-line.active {
  background: var(--green);
}
.wizard-step-line.skipped {
  background: var(--amber);
}

/* STEP CONTENT */
.wizard-step-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.step-header {
  margin-bottom: 28px;
}
.step-title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 800;
  color: var(--dark-blue);
}
.step-subtitle {
  margin: 0;
  font-size: 14px;
  color: #5f6f85;
}

/* Referrer masked name reveal pill */
.referrer-name-reveal {
  margin: 8px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #0a2240;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0fdf4;
  border: 1px solid rgba(22, 163, 74, 0.25);
  border-radius: 8px;
  padding: 7px 14px;
}

.referrer-name-reveal i {
  color: #16a34a;
  font-size: 15px;
}

.referrer-name-reveal strong {
  color: #16a34a;
}

/* INPUT + VERIFY ROW */
.input-verify-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.input-verify-row input {
  flex: 1;
  height: 54px;
  padding: 0 16px;
  font-size: 15px;
  color: var(--charcoal);
  border: 1px solid rgba(10, 34, 64, 0.12);
  border-radius: 12px;
  background: var(--light-gray-bg);
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
  font-family: inherit;
}

.input-verify-row input:focus {
  background: var(--pure-white);
  border-color: var(--cobalt);
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.08);
}

.verify-btn {
  white-space: nowrap;
  height: 44px;
  padding: 0 20px;
  background: var(--cobalt);
  color: var(--pure-white);
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.verify-btn:hover:not(:disabled) {
  background: #0033a0;
}
.verify-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.digilocker-btn {
  background: #0f766e;
}

.digilocker-btn:hover:not(:disabled) {
  background: #0c5f58;
}

.verify-btn.small {
  height: 38px;
  padding: 0 14px;
  font-size: 12px;
}

.verified-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--green);
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.verified-badge i {
  font-size: 18px;
}

.otp-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.otp-input {
  flex: 1;
  height: 48px;
  padding: 0 16px;
  font-size: 18px;
  letter-spacing: 4px;
  text-align: center;
  border: 1px solid rgba(0, 61, 165, 0.3);
  border-radius: 10px;
  background: #f0f6ff;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
}

.otp-input:focus {
  border-color: var(--cobalt);
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.08);
}

.resend-btn {
  background: transparent;
  border: 1px solid rgba(0, 61, 165, 0.3);
  color: var(--cobalt);
  border-radius: 8px;
  padding: 0 14px;
  height: 38px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
}

.resend-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* SECTION DIVIDER */
.section-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 28px 0 20px;
}

.section-divider::before,
.section-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: rgba(0, 61, 165, 0.1);
}

.section-divider span {
  font-size: 12px;
  font-weight: 800;
  color: var(--cobalt);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
}

/* AADHAAR ADDRESS BOX */
.aadhaar-address-box {
  margin-top: 16px;
  border: 1px solid rgba(22, 163, 74, 0.25);
  border-radius: 12px;
  background: #f0fdf4;
  padding: 16px;
}

.aadhaar-address-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 800;
  color: var(--green);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.aadhaar-address-text {
  margin: 0 0 14px;
  font-size: 14px;
  color: var(--dark-blue);
  font-weight: 500;
}

/* Aadhaar address loading state */
.aadhaar-address-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f0f6ff;
  border: 1px solid rgba(0, 61, 165, 0.15);
  color: var(--cobalt);
  font-size: 13px;
  font-weight: 600;
}

.aadhaar-address-loading i {
  font-size: 20px;
}

.spin {
  animation: spin 0.9s linear infinite;
}

.spinner {
  font-size: 44px;
  color: #003da5;
  display: inline-block;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.save-label {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--dark-blue);
}

.checkbox-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.check-option {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--dark-blue);
  cursor: pointer;
}

.check-option input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: var(--cobalt);
  cursor: pointer;
}

/* SLIDE TRANSITION */
.slide-down-enter-active {
  transition: all 0.35s ease;
}
.slide-down-leave-active {
  transition: all 0.25s ease;
}
.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* PAN VERIFIED BOX */
.verified-info-box {
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid rgba(22, 163, 74, 0.25);
  border-radius: 12px;
  background: #f0fdf4;
  padding: 14px 18px;
  margin-top: 14px;
}

.verified-info-box i {
  font-size: 28px;
  color: var(--green);
  flex-shrink: 0;
}
.verified-info-name {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--dark-blue);
}
.verified-info-sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--green);
  font-weight: 600;
}

/* SKIP NOTICE */
.skip-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fffbeb;
  border: 1px solid rgba(234, 179, 8, 0.3);
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #92400e;
  font-weight: 500;
}

.skip-notice i {
  font-size: 16px;
  flex-shrink: 0;
  margin-top: 1px;
}

/* STEP ACTIONS */
.step-actions {
  margin-top: 32px;
}

.step-actions.two-col {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.right-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.next-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 52px;
  padding: 0 32px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  background: #ffc72c;
  border: 2px solid #ffc72c;
  color: #0a2240;
  font-size: 15px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.next-btn:hover:not(:disabled) {
  background: #003da5;
  border-color: #003da5;
  color: #fff;
  box-shadow: 0 4px 16px rgba(0, 61, 165, 0.3);
  transform: translateY(-1px);
}

.next-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 24px;
  background: transparent;
  color: #5a6b82;
  border: 1.5px solid #d1dbe8;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.back-btn:hover {
  border-color: var(--cobalt);
  color: var(--cobalt);
}

.do-later-btn {
  background: transparent;
  color: #5a6b82;
  border: 1.5px solid #d1dbe8;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  padding: 0 20px;
  height: 44px;
  transition: all 0.2s;
}

.do-later-btn:hover {
  border-color: var(--amber);
  color: var(--amber);
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 52px;
  padding: 0 36px;
  background: linear-gradient(135deg, var(--gold) 0%, #f5b000 100%);
  color: var(--dark-blue);
  border: none;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.3s;
  font-family: inherit;
  box-shadow: 0 8px 20px rgba(255, 199, 44, 0.2);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px rgba(255, 199, 44, 0.3);
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* SUMMARY GRID */
.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 28px;
}

.summary-card {
  border: 1px solid #dce7f6;
  border-radius: 14px;
  background: #fafcff;
  padding: 16px;
}

.summary-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #edf2f7;
  font-size: 13px;
  font-weight: 800;
  color: var(--cobalt);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.summary-card-header i {
  font-size: 16px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  border-bottom: 1px solid #f0f4fa;
}

.summary-row:last-child {
  border-bottom: none;
}
.summary-row span {
  color: #5a6b82;
  flex-shrink: 0;
}
.summary-row strong {
  color: var(--dark-blue);
  text-align: right;
  word-break: break-word;
}

.address-row strong {
  font-size: 12px;
  max-width: 60%;
}

.status-ok {
  color: var(--green) !important;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.status-pending {
  color: var(--amber) !important;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.status-warn {
  color: var(--amber) !important;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

/* T&C — DISTRIBUTOR AGREEMENT & CONSENT */
.tnc-box {
  border: 1px solid #dce7f6;
  border-radius: 14px;
  background: #fafcff;
  padding: 20px;
  margin-bottom: 20px;
}

.tnc-title {
  margin: 0 0 18px;
  font-size: 15px;
  font-weight: 800;
  color: var(--dark-blue);
}

/* Phase wrapper */
.tnc-phase {
  margin-bottom: 22px;
  padding-top: 18px;
  border-top: 1px dashed rgba(0, 61, 165, 0.15);
}
.tnc-phase:first-of-type {
  padding-top: 0;
  border-top: none;
}

.tnc-phase-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 800;
  color: var(--dark-blue);
  margin-bottom: 14px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.tnc-phase-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--cobalt);
  color: var(--pure-white);
  font-size: 12px;
  font-weight: 800;
  flex-shrink: 0;
}

/* Phase 1: checklist */
.tnc-checklist {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tnc-checklist-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--pure-white);
  border: 1px solid rgba(0, 61, 165, 0.1);
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.tnc-checklist-item.accepted {
  border-color: rgba(22, 163, 74, 0.45);
  background: #f0fdf4;
}

.tnc-checklist-item:hover {
  border-color: rgba(0, 61, 165, 0.25);
}

.tnc-consent-control {
  flex-shrink: 0;
  margin-top: 2px;
}

.tnc-checklist-item input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: var(--cobalt);
  cursor: pointer;
}

.consent-accept-btn {
  border: 1px solid rgba(0, 61, 165, 0.2);
  background: #eef4ff;
  color: var(--cobalt);
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}

.consent-accept-btn:disabled {
  cursor: default;
  opacity: 0.75;
}

.tnc-checklist-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  line-height: 1.6;
}

.tnc-checklist-text strong {
  color: var(--dark-blue);
  font-weight: 800;
}

.tnc-serial {
  margin-right: 4px;
}

.tnc-checklist-text em {
  color: #344760;
  font-style: normal;
}

/* Phase 2: scrollable declaration */
.tnc-scroll {
  max-height: 220px;
  overflow-y: auto;
  padding-right: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #344760;
  line-height: 1.7;
  background: var(--pure-white);
  border: 1px solid rgba(0, 61, 165, 0.1);
  border-radius: 10px;
  padding: 16px;
}

.tnc-declaration-heading {
  font-size: 13px;
  font-weight: 800;
  color: var(--dark-blue);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin: 0 0 10px;
}

.tnc-dynamic-agreement {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.tnc-section-heading {
  font-size: 13px;
  font-weight: 800;
  color: var(--cobalt);
  margin: 18px 0 8px;
}

.tnc-scroll p {
  margin: 0 0 10px;
}
.tnc-scroll ol {
  margin: 0 0 4px;
  padding-left: 20px;
}
.tnc-scroll ol[type="a"] {
  margin-top: 6px;
}
.tnc-scroll li {
  margin-bottom: 8px;
}

.tnc-scroll::-webkit-scrollbar {
  width: 4px;
}
.tnc-scroll::-webkit-scrollbar-track {
  background: #f0f4fa;
  border-radius: 4px;
}
.tnc-scroll::-webkit-scrollbar-thumb {
  background: #d1dbe8;
  border-radius: 4px;
}

.tnc-scroll-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid rgba(217, 119, 6, 0.3);
  border-left: 3px solid var(--amber);
  border-radius: 8px;
}

.tnc-scroll-hint i {
  font-size: 15px;
  color: var(--amber);
  flex-shrink: 0;
}

.tnc-scroll-complete {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 14px;
  font-size: 12px;
  font-weight: 700;
  color: var(--green);
  background: #f0fdf4;
  border: 1px solid rgba(22, 163, 74, 0.25);
  border-left: 3px solid var(--green);
  border-radius: 8px;
}

.tnc-scroll-complete i {
  font-size: 15px;
  flex-shrink: 0;
}

/* Phase 3: type-to-consent */
.declaration-instruction {
  font-size: 13px;
  color: #344760;
  margin: 0 0 12px;
  line-height: 1.6;
}

.declaration-expected-box {
  font-size: 15px;
  font-weight: 800;
  color: var(--dark-blue);
  background: var(--pure-white);
  border: 2px solid var(--cobalt);
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 14px;
  line-height: 1.6;
  letter-spacing: 0.2px;
  user-select: none;
}

.declaration-textarea {
  width: 100%;
  height: 90px;
  padding: 14px 20px;
  font-size: 14px;
  color: var(--charcoal);
  border: 1px solid rgba(10, 34, 64, 0.12);
  border-radius: 12px;
  background: var(--light-gray-bg);
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;
  font-family: inherit;
  resize: none;
  user-select: text;
}

.declaration-textarea:focus {
  background: var(--pure-white);
  border-color: var(--cobalt);
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.08);
}

/* SUCCESS */
.success-state {
  text-align: center;
  padding: 32px 0;
}

.success-icon {
  font-size: 64px;
  color: var(--green);
  margin-bottom: 16px;
  line-height: 1;
}
.success-state h3 {
  margin: 0 0 12px;
  font-size: 26px;
  font-weight: 800;
  color: var(--dark-blue);
}
.success-state > p {
  color: #5a6b82;
  font-size: 15px;
  line-height: 1.7;
  max-width: 520px;
  margin: 0 auto 28px;
}

.success-cards-row {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 28px;
}

.success-summary-card {
  border: 1px solid #dce7f6;
  border-radius: 14px;
  background: #fafcff;
  padding: 20px 24px;
  min-width: 240px;
  flex: 1;
  max-width: 320px;
  text-align: left;
}

.success-card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 800;
  color: var(--cobalt);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #edf2f7;
}

.success-check-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #94a3b8;
  padding: 5px 0;
}

.success-check-item i {
  font-size: 18px;
  flex-shrink: 0;
}
.success-check-item.ok {
  color: var(--green);
}

.ba-note {
  margin: 12px 0 0;
  font-size: 12px;
  color: #5a6b82;
  line-height: 1.6;
  padding: 10px 12px;
  background: rgba(0, 61, 165, 0.04);
  border-radius: 8px;
  border-left: 3px solid var(--cobalt);
  display: flex;
  gap: 6px;
  align-items: flex-start;
}

.ba-note i {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--cobalt);
}

.shop-now-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  height: 56px;
  padding: 0 48px;
  background: linear-gradient(135deg, var(--gold) 0%, #f5b000 100%);
  color: var(--dark-blue);
  border: none;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  text-decoration: none;
  box-shadow: 0 8px 24px rgba(255, 199, 44, 0.25);
  transition: all 0.3s;
  letter-spacing: 0.2px;
}

.shop-now-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(255, 199, 44, 0.35);
}

.shop-now-btn:disabled {
  background: #e8e8e8;
  color: #6b7280;
  box-shadow: none;
  cursor: not-allowed;
  transform: none;
}

.placement-btn {
  margin-left: 12px;
}

.placement-popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 34, 64, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.placement-popup-box {
  width: min(1100px, 96vw);
  height: min(760px, 92vh);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(10, 34, 64, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.placement-popup-close {
  align-self: flex-end;
  margin: 12px 12px 0;
  height: 38px;
  padding: 0 14px;
  border: 1px solid #d1dbe8;
  border-radius: 999px;
  background: #ffffff;
  color: #0a2240;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  cursor: pointer;
}

.placement-popup-iframe {
  width: 100%;
  flex: 1;
  border: none;
  background: #ffffff;
}

.pan-type-group {
  margin-bottom: 14px;
}

.pan-type-options {
  display: flex;
  gap: 16px;
  align-items: center;
}

.pan-type-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: var(--dark-blue);
  cursor: pointer;
  margin: 20px 6px 10px 0px;
}

.pan-type-option input {
  margin: 0;
  accent-color: var(--cobalt);
  width: 16px;
  height: 16px;
}

.pan-huf-grid {
  margin-bottom: 14px;
}

/* BANK SKIP DIALOG */
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 34, 64, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.dialog-box {
  background: var(--pure-white);
  border-radius: 18px;
  padding: 32px;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 24px 60px rgba(10, 34, 64, 0.15);
  text-align: center;
  animation: dialogIn 0.25s ease;
}

.digilocker-dialog {
  max-width: 520px;
}

.digilocker-dialog-icon {
  color: var(--cobalt);
}

.digilocker-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  flex-wrap: wrap;
}

.digilocker-blocking-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10, 34, 64, 0.55);
  z-index: 10050;
  display: grid;
  place-items: center;
  padding: 20px;
}

.digilocker-blocking-card {
  width: min(420px, 92vw);
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid rgba(0, 61, 165, 0.14);
  box-shadow: 0 24px 60px rgba(10, 34, 64, 0.2);
  padding: 24px 22px;
  text-align: center;
}

.digilocker-blocking-card i {
  font-size: 40px;
  color: var(--cobalt);
}

.digilocker-blocking-card p {
  margin: 10px 0 0;
  color: #0a2240;
  font-size: 14px;
  font-weight: 700;
}

@keyframes dialogIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.dialog-icon {
  font-size: 48px;
  color: var(--amber);
  margin-bottom: 12px;
  line-height: 1;
}
.dialog-title {
  margin: 0 0 10px;
  font-size: 20px;
  font-weight: 800;
  color: var(--dark-blue);
}
.dialog-body {
  margin: 0 0 24px;
  font-size: 14px;
  color: #5a6b82;
  line-height: 1.7;
}

.dialog-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.dialog-cancel-btn {
  height: 44px;
  padding: 0 24px;
  background: transparent;
  border: 1.5px solid #d1dbe8;
  color: #5a6b82;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
}

.dialog-cancel-btn:hover {
  border-color: var(--cobalt);
  color: var(--cobalt);
}

.dialog-ok-btn {
  height: 44px;
  padding: 0 24px;
  background: #003da5;
  border: none;
  color: var(--pure-white);
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
}

.dialog-ok-btn:hover {
  background: #003da5;
}

/* FIELD HELPERS */
.required {
  color: var(--red);
}
.field-error {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--red);
  font-weight: 600;
}
.field-success {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--green);
  font-weight: 600;
}
.field-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: #8a94a6;
}
.step-error {
  margin: 16px 0 0;
  font-size: 13px;
  color: var(--red);
  font-weight: 700;
  padding: 10px 14px;
  background: #fff5f5;
  border-radius: 8px;
  border-left: 3px solid var(--red);
}

.identity-fields {
  margin-top: 8px;
}

/* REGISTER BUTTON (customer) */
.register-btn {
  margin-top: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 54px;
  padding: 0 48px;
  background: linear-gradient(135deg, var(--gold) 0%, #f5b000 100%);
  color: var(--dark-blue);
  border: none;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(255, 199, 44, 0.2);
  transition: all 0.3s ease;
  font-family: inherit;
}

.register-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(255, 199, 44, 0.28);
}
.register-btn:disabled {
  opacity: 0.6;
  background: #e2e8f0;
  color: #94a3b8;
  box-shadow: none;
  cursor: not-allowed;
}

.toast {
  margin-top: 24px;
  padding: 14px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
}

.toast.success {
  background: #f0fdf4;
  color: var(--green);
  border-left: 3px solid var(--green);
}
.toast.error {
  background: #fef2f2;
  color: var(--red);
  border-left: 3px solid var(--red);
}

.toast.warning {
  background: #fffbeb;
  color: var(--amber);
  border-left: 3px solid var(--amber);
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .register-section {
    padding: 40px 0;
  }
  .container {
    padding: 0 16px;
  }
  .title {
    font-size: 26px;
    margin-bottom: 20px;
  }
  .card-box {
    padding: 24px 18px;
  }
  .form-grid {
    grid-template-columns: 1fr;
    gap: 18px;
  }
  .summary-grid {
    grid-template-columns: 1fr;
  }
  .step-actions.two-col {
    flex-direction: column;
    align-items: stretch;
  }
  .right-actions {
    flex-direction: column;
  }
  .next-btn,
  .back-btn,
  .submit-btn,
  .do-later-btn {
    width: 100%;
    justify-content: center;
  }
  .wizard-step-label {
    font-size: 10px;
  }
  .otp-row {
    flex-wrap: wrap;
  }
  .otp-row .verify-btn {
    width: 100%;
  }
  .checkbox-row {
    flex-direction: column;
    gap: 10px;
  }
  .register-btn {
    width: 100%;
    margin-top: 28px;
  }
  .otp-action-row {
    justify-content: center;
  }
  .success-cards-row {
    flex-direction: column;
    align-items: center;
  }
  .success-summary-card {
    max-width: 100%;
    width: auto;
  }
  .shop-now-btn {
    width: auto;
    align-self: center;
  }
  .placement-btn {
    margin-left: 0;
    margin-top: 12px;
  }
  .placement-popup-box {
    width: 100%;
    height: 92vh;
    border-radius: 12px;
  }
  .dialog-actions {
    flex-direction: column;
  }
  .dialog-cancel-btn,
  .dialog-ok-btn {
    width: 100%;
  }
  .digilocker-actions .verify-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .title {
    font-size: 22px;
  }
  .card-box {
    border-radius: 16px;
    padding: 20px 16px;
  }
  .wizard-progress {
    gap: 0;
  }
  .step-title {
    font-size: 18px;
  }
  .wizard-step-line {
    width: 100%;
  }
}
</style>
